import { Params, redirect } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';
import debug from 'debug';

// GQL
import { client } from '../../../../../../gql/client';
import { evictDerivedCaches } from '../../../../../../gql/cacheHelpers';
import { DELETE_MANY_IMAGES } from '../../../../../../gql/mutations/accidents';
// FOLD (At.0001979.0008.02): CREATE_NEW_IMAGE, CONNECT/DISCONNECT_MANY_USERS,
// UPDATE_ACTION_RELATIONSHIPS, UPDATE_POSITION_ACTION y las mutations de
// VehicleOccupant ya no se usan — todo viaja dentro de SAVE_ACTION (tx única).
import { SAVE_ACTION } from '../../../../../../gql/mutations/actions';
import { GET_POSITION_ACTIONS } from '../../../../../../gql/queries/geoPositions/geoPositionActions';
import {
  CREATE_NEW_MATERIAL_UNIT,
  DELETE_MANY_MATERIALS,
  UPDATE_MANY_MATERIALS_UNITS,
} from '../../../../../../gql/mutations/materialUnits';
import {
  CREATE_NEW_ANIMAL_UNIT,
  DELETE_MANY_ANIMAL_UNITS,
  UPDATE_MANY_ANIMAL_UNITS,
} from '../../../../../../gql/mutations/animalUnits';
import {
  CREATE_NEW_AUX_MACHINE_UNIT,
  DELETE_MANY_AUX_MACHINE_UNITS,
  UPDATE_MANY_AUX_MACHINE_UNITS,
} from '../../../../../../gql/mutations/auxMachineUnits';
import { NEW_ANIMAL_UNIT } from '../../../../../../gql/fragments/animalUnit';
import { NEW_AUX_MACHINE_UNIT } from '../../../../../../gql/fragments/auxMachineUnit';
import { NEW_MATERIAL_UNIT } from '../../../../../../gql/fragments/materialUnit';
import { GET_VEHICLE_OCCUPANT_BY_ID } from '../../../../../../gql/queries/vehicles';
import { GET_ORDER_BY_ID } from '../../../../../../gql/queries/orders/orders';
import { GET_ACTION_BY_ID } from '../../../../../../gql/queries/actions/actions';
import { GET_USER_BY_ID } from '../../../../../../gql/queries/users';
import { GET_ASSET_BY_ID } from '../../../../../../gql/queries/actions/assets';
import { GET_ACTIVITY_BY_ID } from '../../../../../../gql/queries/actions/activitys';
import { Reference, StoreObject } from '@apollo/client';
import {
  CREATE_EXTERNAL_WORK,
  DELETE_EXTERNAL_WORKS_BY_IDS,
  UPDATE_EXTERNAL_WORKS,
} from '../../../../../../gql/mutations/externalWork';
import { GET_EXTERNAL_WORK_BY_ID } from '../../../../../../gql/queries/actions/externalWorks';
import { GET_ANIMAL_UNIT_BY_ID } from '../../../../../../gql/queries/actions/animals';
import { FETCH_EXTERNAL_COMPANIES } from '../../../../../../gql/queries/actions/externalCompanies';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
  directoryNaming,
} from '../../../../../../components/forms/ActionForms/constants';

// Utils
import {
  decompressSessionStorage,
  compressSessionStorage,
} from '../../../../../../utils/sessionStorage';
import {
  UploadImage,
  compareSketchJson,
  CloudinaryUploadError,
} from '../../../../../../utils/image';
import { getClassificationCode } from '../../../../../../utils/orders';
import { assertDraftOwner } from '../../../../../../utils/draftOwnership';
import { ACTION_VALIDATION_SCOPE } from '../../../../../../utils/draftScopes';
import { saveFilesIntoADirectory } from '../../../../../../utils/files';
import {
  addImageUploadOperation,
  OFFLINE_PENDING_UPLOADS_DIR,
} from '../../../../../../offline/offlineImageUploadQueue';
import { enqueuePhotosForUpload } from '../../../../../../utils/enqueuePhotos';
import { holdOrderUploads } from '../../../../../../offline/orderUploadChain';
import {
  queueValidationEdit,
  VALIDATION_IMAGES_RESOLVED_SNAPSHOT,
} from './validationQueue';
import { resolveValidationSave } from './validationSaveState';
import { isOnlineNow } from '../../../../../../offline/connectivity';
import { isActuallyOnline } from '../../../../../../offline/checkConnectivity';
import { createOfflineQueuedError } from '../../../../../../offline/isOfflineQueuedError';
import {
  createPhaseDeadline,
  phaseScopedClient,
  raceWithDeadline,
  isTransportFailure,
} from '../../../../../../offline/phase2Transport';
import { getSketchFromSessionStorage } from '../../../../../../utils/sketch';
import {
  draftSketchFile,
  draftSketchFileName,
  pickSketchFile,
  removeDraftSketchFile,
} from '../../../../../../utils/sketchFile';
import { screenFormContext, type FormContext } from '../../../../../../offline/formStorage';
import i18n from '../../../../../../i18n';

import { externalCompaniesNaming } from '../../../../../../components/display/tables/formTables/ExternalCompaniesSelectionTable/constants';
import {
  findNewAndUpdatedExternalCompanies,
  findRemovedExternalCompaniesIds,
  findNewAndUpdatedAnimals,
  findRemovedAnimalIds,
} from './utils';

// Types
import { OrderState, OrderType } from '../../../../../../types/orders';
import { DataState } from '../../../../../../components/forms/ActionForms/types';
import { Role } from '../../../../../../types/auth';
import {
  tableIds,
  takeBase,
} from '../../../../../../components/display/tables/types';

// Redux
import { store } from '../../../../../../state/configureStore';
import { actionTabsReset } from '../../../../../../state/actions/ui/actionTabs';
import { setCursor } from '../../../../../../state/actions/ui/cursorTables';
import { getCursor } from '../../../../../../state/selectors/ui/tables';
import { orderCloseSetPending, orderCloseRemoveDeclined } from '../../../../../../state/actions/ui/orderCloseConfirmation';
import {
  ADD_BUDGET_ITEM_TO_ACTION,
  UPDATE_ACTION_BUDGET_ITEM_QUANTITY,
  REMOVE_BUDGET_ITEM_FROM_ACTION,
  ADD_MANY_BUDGET_ITEMS_TO_ACTION,
  UPDATE_MANY_ACTION_BUDGET_ITEM_QUANTITIES,
} from '../../../../../../gql/mutations/budget/actionBudgetItems';
import { omitCrewFieldsForBasicAction } from '../../../../../../utils/order';

/**
 * Guarda los datos de una actuación con validación condicional según el rol del usuario.
 *
 * ## FLUJO DE PERMISOS (CRÍTICO PARA SEGURIDAD)
 *
 * ### Roles y sus capacidades:
 *
 * **Role.Tablet** - Operarios de campo (SOLO EDICIÓN):
 * - ✅ Puede editar actuaciones existentes
 * - ✅ Puede guardar cambios (isValidate: false)
 * - ❌ NO puede validar actuaciones (backend rechaza isValidate: true)
 * - ❌ NO puede cambiar estado a Validated
 * - 📝 Los cambios se guardan pero la actuación permanece sin validar
 * - 👥 Un supervisor debe validar posteriormente
 *
 * **Role.RoomTeam / Role.Coex / Role.SuperAdmin** - Supervisores (EDICIÓN + VALIDACIÓN):
 * - ✅ Puede editar actuaciones existentes
 * - ✅ Puede validar actuaciones (isValidate: true)
 * - ✅ Puede cambiar estado a Validated
 * - ✅ Los cambios se guardan Y la actuación queda validada
 *
 * ### Implementación de seguridad:
 *
 * 1. **Frontend** (este archivo, línea ~960):
 *    ```typescript
 *    isValidate: userRole !== Role.Tablet
 *    ```
 *    - Si el usuario es Tablet → isValidate: false
 *    - Si el usuario es supervisor → isValidate: true
 *
 * 2. **Backend** (actions.resolver.ts):
 *    - GraphQL resolver valida el rol del usuario antes de procesar
 *    - Si Tablet intenta forzar isValidate: true → lanza ForbiddenException
 *    - Mensaje de error claro en español explicando restricción
 *
 * 3. **Backend defensa en profundidad** (actions.service.ts):
 *    - Service layer valida nuevamente el rol del usuario
 *    - Doble validación para prevenir bypass si se llama al service directamente
 *    - Garantiza seguridad incluso si se saltan los guards del resolver
 *
 * ### Estados de la actuación:
 *
 * - **OrderState.Open / PendingWork / WorkInProgress**: Actuación en progreso
 * - **OrderState.PendingValidation**: Esperando validación de supervisor
 * - **OrderState.Validated**: Actuación validada ✅ (solo supervisores)
 * - **OrderState.Annulled**: Actuación anulada
 * - **OrderState.End**: Actuación finalizada
 *
 * ### Ejemplo de uso:
 *
 * ```typescript
 * // Usuario Tablet editando:
 * // - isValidate: false (automático)
 * // - Estado: NO cambia a Validated
 * // - Mensaje: "Cambios guardados. Un supervisor validará posteriormente."
 *
 * // Usuario RoomTeam validando:
 * // - isValidate: true (automático)
 * // - Estado: Cambia a Validated
 * // - Mensaje: "Actuación validada correctamente"
 * ```
 *
 * ### Notas importantes:
 *
 * - La validación de rol ocurre en TRES capas (frontend, resolver, service)
 * - Backend NUNCA confía en el frontend (valida independientemente)
 * - Logs detallados en consola para debugging (namespace: rois:actions:save:*)
 * - Errores de validación lanzan ForbiddenException con mensajes claros
 *
 * @param {FormData} formData - Datos del formulario enviado por el usuario
 * @returns {Promise<void>} Promesa que se resuelve cuando el guardado es exitoso
 * @throws {ForbiddenException} Si usuario Tablet intenta validar (backend)
 * @throws {Error} Si hay errores de red o base de datos
 *
 * @see {@link /Users/tastafur/workspace/rois/rois-back/src/modules/action/actions.resolver.ts} - Validación en GraphQL resolver
 * @see {@link /Users/tastafur/workspace/rois/rois-back/src/modules/action/actions.service.ts} - Validación en service layer
 */

/**
 * Detección de error encolado por OfflineQueueLink.
 *
 * Cuando una mutation Apollo se ejecuta offline, OfflineQueueLink la
 * encola en IndexedDB y resuelve el `phaseClient.mutate()` con un Observable
 * error que tiene `offlineQueued: true`. NO es un error real — la mutation
 * se ejecutará al reconectar.
 *
 * Sin esta detección, los catches de los bloques de budget items
 * (`addBudgetItemsChangesItems`, `updateBudgetItemsChangesItems`,
 * `deleteBudgetItemsChangesIds`) interpretaban el `offlineQueued` como
 * fallo real y hacían `throw error`, interrumpiendo el saveData ANTES de
 * llegar al `setItem(NEW_VALIDATION_ACTION_DATA, isActionEdited='true')`.
 *
 * Resultado del bug pre-fix: tras añadir un budget offline, el usuario
 * volvía al summary con `isActionEdited` ausente → loader fallback a
 * `'false'` → mostraba botones del PRIMER resumen ("Continuar"/"Volver")
 * en lugar del segundo ("Editar"/"Guardar").
 */
function isOfflineQueuedError(err: unknown): boolean {
  const e = err as {
    offlineQueued?: boolean;
    message?: string;
    networkError?: { offlineQueued?: boolean };
  };
  return (
    e?.offlineQueued === true ||
    e?.networkError?.offlineQueued === true ||
    (e?.message ?? '').includes('Mutation queued for offline sync')
  );
}

export const saveData = async (
  formData: FormData,
  params?: Params<string>,
  // Replay offline: clientOpId ESTABLE derivado de op.id + isReplay para
  // desactivar los caminos que solo tienen sentido en uso interactivo.
  // `form`: dónde leer y escribir el formulario. La pantalla usa el de siempre
  // (`sessionStorage` y las carpetas OPFS); el reintento de la cola, uno en
  // memoria con la foto de la operación, que no pisa el formulario abierto
  // (spec outbox-autocontenido v2 § 5.2).
  opts?: { clientOpId?: string; isReplay?: boolean; form?: FormContext }
) => {
  const form = opts?.form ?? screenFormContext;
  // Configure debug namespaces
  const logSave = debug('rois:actions:save:main');
  const logOperators = debug('rois:actions:save:operators');
  const logVehicles = debug('rois:actions:save:vehicles');
  const logAnimals = debug('rois:actions:save:animals');
  const logMaterials = debug('rois:actions:save:materials');
  const logMachineries = debug('rois:actions:save:machineries');

  // Plazo de UNA fase para todo lo que viene (spec 2026-09-11 § 1 E1): un solo
  // `AbortController` que viaja en el `fetchOptions` de cada llamada. Al vencer,
  // todo lo que esté en vuelo aborta a la vez. Un techo POR llamada no sirve:
  // este guardado encadena 35 y la espera sumaría minutos.
  const deadline = createPhaseDeadline();
  const phaseClient = phaseScopedClient(client, deadline);
  // A partir de aquí las escrituras ya están hechas: un fallo tardío NO puede
  // re-encolar (las repetiría). Ver E7.
  let writesCommitted = false;


  // Enable logs
  debug.enable('rois:actions:save:*');

  logSave('🚀 Iniciando guardado de datos validados');

  // BUG #12 FIX: Fallback cuando sessionStorage está vacío (después de logout/login)
  const actionDataStorageRaw = form.store.getItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA
  );

  let orderActionData;

  if (actionDataStorageRaw) {
    // Caso normal: sessionStorage tiene datos
    orderActionData = JSON.parse(decompressSessionStorage(actionDataStorageRaw));
  } else if (params?.id) {
    // Fallback: sessionStorage vacío pero tenemos params.id de la URL
    logSave('⚠️ SessionStorage vacío, obteniendo datos desde servidor...');
    const {
      data: { action },
    } = await phaseClient.query({
      query: GET_ACTION_BY_ID,
      variables: { id: params.id },
    });

    const {
      data: { order },
    } = await phaseClient.query({
      query: GET_ORDER_BY_ID,
      variables: { id: action.orderIDs[0] },
    });

    orderActionData = {
      id: params.id,
      orderId: order.id,
      roid: order.roid,
      isActionEdited: 'false',
    };
    logSave('✅ Datos obtenidos desde servidor');
  } else {
    // Error: no hay datos disponibles
    const error = new Error('No se encontró datos de la acción ni en sessionStorage ni en params');
    Sentry.captureException(error);
    throw error;
  }

  // 🔴 Barrera: nunca se escribe sobre una actuación que no es la de la URL.
  // Los loaders ya descartan el contexto de otra actuación
  // (`reconcileValidationActionContext`), así que esto no debería saltar
  // nunca; si salta, cortar aquí evita validar la actuación equivocada y que
  // Partidas escriba en dos a la vez (diagnóstico 2026-10-08 § 8.1). Solo en
  // el guardado en vivo: en la reproducción de la cola sin conexión no se
  // añade ningún rechazo nuevo (`.claude/rules/guard-nuevo-cola-offline.md`).
  if (
    opts?.isReplay !== true &&
    params?.id &&
    orderActionData?.id &&
    orderActionData.id !== params.id
  ) {
    const error = new Error(
      `El contexto de validación es de otra actuación (${orderActionData.id}) y no de la de la URL (${params.id}); no se guarda nada.`
    );
    Sentry.captureException(error, {
      tags: { guard: 'validation-context-mismatch' },
    });
    throw error;
  }

  // 🔴 Barrera 2: las copias de las pestañas tienen que ser de esta actuación.
  // Sin contexto principal (se entró por el recorrido, se recargó, una URL) la
  // de arriba no ve nada; esta mira el dueño que deja
  // `reconcileValidationActionContext`. No debería saltar nunca; si salta,
  // cortar evita mandar el PK y la descripción de otra actuación sobre esta
  // (invariante I3 del spec 2026-11-02 wizard-draft-ownership). Lanza
  // `DraftOwnerMismatchError` con la etiqueta de Sentry
  // `draft-owner-mismatch:action-validation`. Misma regla que la de arriba:
  // solo en vivo — en el reintento de la cola no hace nada.
  assertDraftOwner(ACTION_VALIDATION_SCOPE, params?.id, {
    isReplay: opts?.isReplay === true,
  });

  logSave(' INICIANDO GUARDADO DE ACCIÓN');
  logSave(` ID de la acción: ${orderActionData.id}`);
  logSave(` ID de la orden: ${orderActionData.orderId}`);

  // Extract key data from orderActionData for logging
  const assetSwitchState = orderActionData?.editionAssetSwitch === 'true';

  logSave(
    ` Estado de switch Actius: ${assetSwitchState ? 'Activo' : 'Inactivo'}`
  );

  // ── OFFLINE BRANCH (espejo del editar-accidente): capturar todo y encolar ──
  // Sin esto, el editar/validar offline disparaba ~20 mutations encadenadas y
  // la PRIMERA encolada lanzaba offlineQueued → el catch intermedio "continuaba"
  // saltándose external works/unidades, y el caller (C-11.9) redirigía como
  // éxito → guardado parcial silencioso (clase At.0001979.0008.02).
  // Sondeo previo (spec 2026-09-11 § 1 E2/E3): `isOnlineNow()` como fuente
  // única —en Android manda el nativo— y, si dice que hay red, una comprobación
  // real de 2,5 s contra el backend. Con cobertura mala `navigator.onLine`
  // sigue diciendo `true`, así que sin el sondeo este asistente se colgaba
  // hasta que el sistema se rendía (medido: 6 min sin avisar).
  const isReplay = opts?.isReplay === true;
  const online = isOnlineNow();
  const reachable = online && !isReplay ? await isActuallyOnline() : online;
  if (!reachable) {
    if (isReplay) {
      // En replay NO re-encolar (op.id nuevo → clientOpId nuevo → duplicados).
      // Lanzar → el sync manager lo trata como transient y conserva la op.
      throw new Error('Replay sin conexión — se reintentará');
    }
    logSave('📴 Modo offline — encolando EDICIÓN de actuación para sync posterior');

    const { redirectTo } = await queueValidationEdit({
      formData,
      params,
      orderId: orderActionData.orderId as string,
      orderRoid: orderActionData.roid ?? '',
      actionId: orderActionData.id as string,
      withImages: true,
    });
    window.dispatchEvent(new CustomEvent('offline-action-queued'));
    // Centinela compartido: el `action.ts` avisa con toast y redirige. Antes
    // se redirigía aquí en silencio (el evento no lo escucha nadie).
    throw createOfflineQueuedError({ redirectTo });
  }

  // BUG #12 FIX Fase 2: Fallback para sessionStorage vacío después de logout/login
  const externalWorksStorageRaw = form.store.getItem(
    formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM
  );

  // [PARTIAL-DEBUG] Log all sessionStorage keys for validation tabs
  console.log('[PARTIAL-DEBUG] === saveData FINAL ejecutándose ===');
  console.log('[PARTIAL-DEBUG] Action state:', orderActionData?.state);
  const allValidationKeys = Object.entries(formActionValidationNaming);
  allValidationKeys.forEach(([name, key]) => {
    const val = form.store.getItem(key);
    console.log(`[PARTIAL-DEBUG] SS ${name}: ${val ? `EXISTS (${val.length} chars)` : '❌ EMPTY'}`);
  });

  const crewFormMissing = !externalWorksStorageRaw;
  let externalWorksValidation;
  if (externalWorksStorageRaw) {
    externalWorksValidation = JSON.parse(
      decompressSessionStorage(externalWorksStorageRaw)
    );
    console.log('[PARTIAL-DEBUG] Operators from SS:', {
      operatorsCount: externalWorksValidation?.operatorsTable?.length,
      externalsCount: externalWorksValidation?.externals?.length,
      isExternal: externalWorksValidation?.isExternal,
      vehicleKeys: Object.keys(externalWorksValidation?.vehicleTable || {}),
    });
  } else {
    // Sin formulario de operarios = la pestaña no se tocó (la página lo
    // precarga del servidor; solo falta si esa precarga falló, o en la
    // básica, que no tiene la pestaña). El objeto vacío es solo para que lo
    // de abajo no reviente: `crewFormMissing` hace que NADA de la tripulación
    // viaje. Antes este relleno mandaba `vehicles: []` (borra todos los
    // vehículos con conductor) y una lista de externas vacía (borra todas las
    // empresas externas). Spec: docs/superpowers/specs/2026-10-07-wizard-tab-jump-design.md
    logSave('⚠️ externalWorksValidation sessionStorage vacío: la tripulación no se toca');
    console.log('[PARTIAL-DEBUG] ⚠️ OPERATORS SESSIONSTORAGE EMPTY — crew left untouched');
    externalWorksValidation = {
      operatorsTable: [],
      externals: [],
      vehicleTable: {},
      isExternal: false,
    };
  }

  // Log operator information
  if (externalWorksValidation) {
    const internalOperators =
      externalWorksValidation.operatorsTable || [];
    const externalOperators = externalWorksValidation.externals || [];
    const isExternal = externalWorksValidation.isExternal === true;

    logOperators(
      ` Tipo de operadores seleccionado: ${
        isExternal ? 'Externos' : 'Internos'
      }`
    );
    logOperators(` Operadores internos: ${internalOperators.length}`);
    logOperators(` Operadores externos: ${externalOperators.length}`);

    if (internalOperators.length > 0) {
      logOperators(' Detalle de operadores internos:');
      internalOperators.forEach((op: any, idx: number) => {
        if (op && op.id) {
          logOperators(`  [${idx}] ID: ${op.id}, Nombre: ${op.name || 'N/A'}`);
        }
      });
    }

    if (externalOperators.length > 0) {
      logOperators(' Detalle de operadores externos:');
      externalOperators.forEach((comp: any, idx: number) => {
        logOperators(
          `  [${idx}] Empresa: ${comp.company || 'N/A'}, Operarios: ${
            comp.operatorsNumber || 0
          }`
        );
      });
    }

    // Log vehicle information
    const vehicles = externalWorksValidation.vehicleTable || {};
    logVehicles(` Total de vehículos: ${Object.keys(vehicles).length}`);

    if (Object.keys(vehicles).length > 0) {
      logVehicles(' Detalle de vehículos:');
      Object.entries(vehicles).forEach(
        ([vehicleId, vehicleData]: [string, any]) => {
          logVehicles(
            `  ID: ${vehicleId}, Conductor: ${
              vehicleData.driver || 'Sin conductor'
            }`
          );
        }
      );
    }
  }

  // Action data
  // BUG #12 FIX Fase 2: Forzar lectura fresca de DB para evitar IDs obsoletos del cache
  const {
    data: { action },
  } = await phaseClient.query({
    query: GET_ACTION_BY_ID,
    variables: {
      id: orderActionData.id,
    },
    fetchPolicy: 'network-only',
  });

  // Redux data
  const dispatch = store.dispatch;
  const cursorMultimedia = getCursor(tableIds.MULTIMEDIA)(store.getState());

  // Location data
  const pkInit = formData.get('actionPkInit');
  const pkInitRaw =
    pkInit && typeof pkInit === 'string' && pkInit.replace(/ /g, '').split('+');
  const pkEnd = formData.get('actionPkEnd');
  const pkEndRaw =
    pkEnd && typeof pkEnd === 'string' && pkEnd.replace(/ /g, '').split('+');

  const concessionId = formData.get('actionConcession');
  const track = formData.get('actionTrack');
  const roadId = formData.get('actionRoad');
  const lane = formData.get('actionLane');
  const direction = formData.get('actionDirection');
  const margin = formData.get('actionMargin');
  // C-11.13: leer las coordenadas marcadas por el botón "geoposición" del
  // LocationForm. Antes este valor se ignoraba en edit → usuario clicaba el
  // botón verde, parecía que se guardaba pero las coords no llegaban al back.
  const actionCoordinates = formData.get('actionCoordinates');

  // Edition data
  const editionActivity = formData.get('editionActivity');
  const editionElementOrder = formData.get('editionElementOrder');
  const editionCapitol = formData.get('editionCapitol');
  const editionSubCapitol = formData.get('editionSubCapitol');
  const editionAsset = formData.get('editionAsset');
  const editionAssetSwitch = formData.get('editionAssetSwitch');
  const editionDescription = formData.get('editionDescription');
  const editionLaneCutting = formData.get('editionLaneCutting');
  // C-11.13: distinguir "no presente" vs "presente con valor null" para
  // sketch. La form sólo incluye `editionSketch` en formData cuando cambió
  // respecto al valor del server (vía `difference()` en
  // `AnimalsValidationFormPage.onFormSubmit`). Si NO está presente → usuario
  // no tocó el sketch → no procesar (`undefined` indica "sin información").
  // Si está presente con valor null → usuario lo borró explícitamente → DELETE.
  // Si está presente con string → usuario repintó → save+remove.
  const sketchInFormData = formData.has('editionSketch');
  const editionSketch = sketchInFormData
    ? formData.get('editionSketch')
    : undefined;
  const editionPredefine = formData.get('editionPredefine');
  // Operario que registra (editable, no obligatorio). Solo llega en formData
  // cuando AnimalsForm detectó que cambió respecto al valor del servidor.
  const registeredByUserId = formData.get('registeredByUserId');

  // Hours data
  const registerInitDate = formData.get('registerInitDate');
  // Fechas opcionales borrables (papelera): tres estados distintos.
  //   ausente del formData (null de formData.get) → undefined = no tocar
  //   ''                                          → null = borrar en BD
  //   valor                                       → persistir
  // registerEndDate vive en la Order y registerArrivalDate en la Action;
  // saveAction escribe cada lado dentro de la misma $transaction.
  const registerEndDate = formData.get('registerEndDate');
  // Hora de llegada (opcional). No afecta al cálculo de minutos.
  const registerArrivalDate = formData.get('registerArrivalDate');
  const hoursType = formData.get('hoursType');
  const totalHours = formData.get('totalHours') as string;
  const splitHourAndMinutes = totalHours?.split(':');
  const minutesTotals = splitHourAndMinutes
    ? parseInt(splitHourAndMinutes[0]) * 60 + parseInt(splitHourAndMinutes[1])
    : undefined;
  // Ausente ≠ nulo. `formData.get` devuelve null cuando el campo no viajó y
  // `JSON.parse(null)` también da `null`, que el backend escribe TAL CUAL en
  // base de datos (su condición es `input.isExternal !== undefined`). Sin
  // campo → `undefined` → no se toca.
  const isExternalRaw = formData.get('isExternal');
  const isExternal =
    isExternalRaw === null ? undefined : JSON.parse(isExternalRaw as string);
  // Actuación simultánea: el backend deriva las horas a 0 al reconciliar.
  // Ausente → `undefined` (no tocar), como `isExternal`: solo falta cuando no
  // hay formulario de operarios, y un `false` inventado desmarcaría la
  // «Simultánea» guardada.
  const isSimultaneousRaw = formData.get('isSimultaneous');
  const isSimultaneous =
    isSimultaneousRaw === null
      ? undefined
      : JSON.parse(isSimultaneousRaw as string);
  // Actuació bàsica: tripulación y medios son del Recorregut de Vigilància, y
  // ninguno de sus campos debe viajar (ver `omitCrewFieldsForBasicAction`).
  const isBasicActionSubmit = formData.get('isBasicAction') === 'true';
  const userRole = formData.get('userRole') as Role | null;
  const submitIntent = formData.get('intent') as
    | 'save'
    | 'validate'
    | 'pending-certification'
    | null;
  const tablesDataRaw = form.store.getItem(
    formActionValidationNaming.DATA_CHANGES
  );

  // Tables data
  const tablesData = tablesDataRaw
    ? JSON.parse(decompressSessionStorage(tablesDataRaw))
    : {};

  // Log de tablas disponibles
  logSave('📊 Tablas de datos disponibles:');
  logSave(`   materialsData: ${!!tablesData?.materialsData}`);
  logSave(`   machineryData: ${!!tablesData?.machineryData}`);
  logSave(`   vehiclesData: ${!!tablesData?.vehiclesData} (vehículos de obra)`);
  logSave(`   budgetItemsData: ${!!tablesData?.budgetItemsData}`);
  logSave(`   animalsData: ${!!tablesData?.animalsData}`);
  logSave(`   editionData: ${!!tablesData?.editionData} (imágenes)`);

  // Budget Items data - Seguir patrón de Materials
  const sessionStorageRaw = form.store.getItem('data-changes');
  if (sessionStorageRaw) {
    try {
      const sessionStorageParsed = JSON.parse(
        decompressSessionStorage(sessionStorageRaw)
      );
      logSave('Processing budget items from sessionStorage');
    } catch (error) {
      logSave(
        'Error parsing budget items from sessionStorage: %s',
        (error as Error).message
      );
    }
  }

  const addBudgetItemsChangesItems = tablesData?.budgetItemsData &&
    tablesData.budgetItemsData?.[DataState.added] && [
      ...tablesData.budgetItemsData[DataState.added],
    ];
  const updateBudgetItemsChangesItems = tablesData?.budgetItemsData &&
    tablesData.budgetItemsData?.[DataState.updated] && [
      ...tablesData.budgetItemsData[DataState.updated],
    ];
  const deleteBudgetItemsChangesIds = tablesData?.budgetItemsData &&
    tablesData.budgetItemsData?.[DataState.deleted] && [
      ...tablesData.budgetItemsData[DataState.deleted],
    ];

  logSave(
    'Budget items changes - Add: %d, Update: %d, Delete: %d',
    addBudgetItemsChangesItems?.length || 0,
    updateBudgetItemsChangesItems?.length || 0,
    deleteBudgetItemsChangesIds?.length || 0
  );

  // Log budget items changes
  if (addBudgetItemsChangesItems?.length > 0) {
    logSave(`📋 Partidas a añadir: ${addBudgetItemsChangesItems.length}`);
  }
  if (updateBudgetItemsChangesItems?.length > 0) {
    logSave(
      `📋 Partidas a actualizar: ${updateBudgetItemsChangesItems.length}`
    );
    logSave(
      '📋 Estructura de partidas a actualizar:',
      updateBudgetItemsChangesItems
    );
  }
  if (deleteBudgetItemsChangesIds?.length > 0) {
    logSave(`📋 Partidas a eliminar: ${deleteBudgetItemsChangesIds.length}`);
  }

  // External companies data
  const currentExternalWorks = await Promise.all(
    action.externalWorksIDs.map(async (externalWorkId: string) => {
      const {
        data: { externalWork },
      } = await phaseClient.query({
        query: GET_EXTERNAL_WORK_BY_ID,
        variables: {
          id: externalWorkId,
        },
      });

      return externalWork;
    })
  );

  const modificationExternalCompanies = externalWorksValidation?.externals?.map(
    (external: { externalCompanyIDs: string[]; id: string }) => ({
      ...external,
      externalCompanyIDs: [external?.externalCompanyIDs?.[0] ?? external.id],
    })
  );

  // Sin formulario de operarios no hay lista de externas que comparar: una
  // vacía se leería como «las quitó todas» y las borraría.
  const { worksToAdd, worksToUpdate } = crewFormMissing
    ? { worksToAdd: [], worksToUpdate: [] }
    : findNewAndUpdatedExternalCompanies(
        currentExternalWorks,
        modificationExternalCompanies
      );

  const worksToDelete = crewFormMissing
    ? []
    : findRemovedExternalCompaniesIds(
        currentExternalWorks,
        modificationExternalCompanies
      );

  // Animals table data
  //
  // 🔴 Actuació bàsica (2026-08-11): NO registra fauna, y su pestaña ya no se
  // monta — así que su `sessionStorage` NO EXISTE. Sin este guard,
  // `animalsTable` caería a `[]` y `findRemovedAnimalIds` leería eso como «el
  // usuario las quitó todas»: el primer *Validar* BORRARÍA toda la fauna que
  // la básica tuviera guardada de antes. La decisión es la contraria — el dato
  // se queda quieto.
  //
  // De paso cierra la otra puerta: un borrador de fauna de una actuación
  // NORMAL abandonada sobrevive en sessionStorage y se colaría aquí como
  // altas fantasma (misma clase de fallo que la «tripulación fantasma»).
  //
  // Spec: docs/superpowers/specs/2026-08-11-basica-sin-fauna-design.md
  const animalsTableDataRaw = isBasicActionSubmit
    ? null
    : form.store.getItem(
        formActionValidationNaming.NEW_VALIDATION_ANIMALS_FORM
      );

  const animalsTable = animalsTableDataRaw
    ? Object.values(
        JSON.parse(decompressSessionStorage(animalsTableDataRaw))
          ?.animalsTable || {}
      )
    : [];

  const currentAnimals = isBasicActionSubmit
    ? []
    : await Promise.all(
        action.animalUnitIDs.map(async (id: string) => {
          const {
            data: { animalUnit },
          } = await phaseClient.query({
            query: GET_ANIMAL_UNIT_BY_ID,
            variables: {
              id,
            },
          });

          return {
            animalUnitId: animalUnit.id,
            id: animalUnit.animalIDs[0],
            statusId: animalUnit.animalStatusIDs[0],
            ut: animalUnit.unit,
          };
        })
      );

  const { animalsToUpdate, animalsToAdd } = isBasicActionSubmit
    ? { animalsToUpdate: [], animalsToAdd: [] }
    : findNewAndUpdatedAnimals(currentAnimals, animalsTable as any);
  const animalsToDelete = isBasicActionSubmit
    ? []
    : findRemovedAnimalIds(currentAnimals, animalsTable as any);

  // Images
  const imagesToAdd =
    tablesData?.editionData && tablesData.editionData?.[DataState.added];
  const imagesToDelete =
    tablesData?.editionData && tablesData.editionData?.[DataState.deleted];

  // C-11.13: leer el sketch ORIGINAL desde el sessionStorage correcto.
  //
  // ANTES: `orderActionData?.editionSketch` apuntaba a `NEW_VALIDATION_ACTION_DATA`
  // que sólo guarda IDs de navegación (`{ id, orderId, roid, isActionEdited, ... }`).
  // El sketch original NUNCA estaba ahí → `originalSketch` siempre `undefined`.
  // `compareSketchJson(modified, undefined)` caía en la rama "nuevo sketch a
  // guardar", se re-subía el blob OPFS a Cloudinary y se disparaba un
  // `createImage` espurio en cada save aunque el usuario no hubiese tocado nada.
  // Además el sketch viejo no se borraba (sketchToRemove=null), acumulando
  // huérfanos en `Order.imageIDs[]`.
  //
  // El sketch real persistido por el server vive en
  // `NEW_VALIDATION_EDITION_FORM.dataFromServer.editionSketch` (lo escribe
  // `Edition/loader.ts`/`ActionPage/loader.ts` al pre-cargar datos).
  const editionFormRaw = form.store.getItem(
    formActionValidationNaming.NEW_VALIDATION_EDITION_FORM,
  );
  let originalSketch: string | null = null;
  if (editionFormRaw) {
    try {
      const editionFormParsed = JSON.parse(
        decompressSessionStorage(editionFormRaw),
      );
      originalSketch =
        (editionFormParsed?.dataFromServer?.editionSketch as string | null) ??
        null;
    } catch {
      // sessionStorage corrupto → tratar como "sin sketch original" (caso
      // edge muy raro; el peor síntoma es un re-save inocuo, no peor que
      // el comportamiento previo a este fix).
      originalSketch = null;
    }
  }
  // Si `editionSketch` no estaba en formData (usuario no tocó el sketch),
  // saltarse `compareSketchJson` para evitar interpretar la ausencia como
  // "borrado". Sólo evaluamos cambios cuando la form indica explícitamente
  // que tocó el sketch (presente con valor o presente con null).
  const { sketchToSaveOrUpdate, sketchToRemove } = sketchInFormData
    ? compareSketchJson(editionSketch as string | null, originalSketch)
    : { sketchToSaveOrUpdate: null, sketchToRemove: null };

  // Operators data
  //
  // FOLD (At.0001979.0008.02): `newOperatorsIDs` es el set COMPLETO deseado que
  // sale del form. Viaja como `saveAction.input.userIds` (el backend hace
  // `set`-reconcile: conecta los nuevos y desconecta los ausentes en la misma
  // tx). Los diffs add/delete (CONNECT/DISCONNECT_MANY_USERS) sobran.
  const newOperatorsIDs = externalWorksValidation.operatorsTable
    .filter((operator: null | { id: string }) => operator)
    .filter((operator: { id: string }) => operator.id)
    .map((item: { id: string }) => item.id);

  // VehiclesData
  //
  // FOLD: set COMPLETO deseado de pares {vehicleId, driverId} del form
  // (incluye los occupants existentes que siguen). Viaja como
  // `saveAction.input.vehicles` — el backend borra los VehicleOccupant
  // actuales y crea estos en la misma tx (reemplazo total, la MISMA semántica
  // que tenía la cadena DELETE_MANY_VEHICLE_OCCUPANTS + NEW_VEHICLE_OCCUPANT).
  const newVehiclesData = (
    Object.values(externalWorksValidation.vehicleTable) as any[]
  )
    .filter(
      (vehicle: ArrayLike<unknown> | { [s: string]: unknown }) =>
        Object.entries(vehicle).length
    )
    .map(
      (vehicle: {
        driverID: string;
        driver: string;
        vehicleID: string;
        id: string;
        vehicleOccupantID: string;
      }) => ({
        driverId: vehicle.driverID || vehicle.driver,
        vehicleId: vehicle.vehicleID || vehicle.id,
        vehicleOccupantId: vehicle.vehicleOccupantID,
      })
    )
    .filter((vehicle: { driverId: string }) => vehicle.driverId);

  // Materials data
  const addMaterialsChangesItems = tablesData?.materialsData &&
    tablesData.materialsData?.[DataState.added] && [
      ...tablesData.materialsData[DataState.added],
    ];
  const updateMaterialsChangesItems = tablesData?.materialsData &&
    tablesData.materialsData?.[DataState.updated] && [
      ...tablesData.materialsData[DataState.updated],
    ];

  const updateMaterialsChangesItemsFiltered = updateMaterialsChangesItems?.map(
    (materialUpdated: { unitId: string; newValue: number }) => {
      return {
        materialUnitId: materialUpdated.unitId,
        newValue: materialUpdated.newValue,
      };
    }
  );

  const deleteMaterialsChangesIds = tablesData?.materialsData &&
    tablesData.materialsData?.[DataState.deleted] && [
      ...tablesData.materialsData[DataState.deleted],
    ];

  // Machinery data
  logMachineries('🔧 ========== EXTRAYENDO DATOS DE MAQUINARIA ==========');
  const addMachineryChangesItems = tablesData?.machineryData &&
    tablesData.machineryData?.[DataState.added] && [
      ...tablesData.machineryData[DataState.added],
    ];
  const updateMachineryChangesItems = tablesData?.machineryData &&
    tablesData.machineryData?.[DataState.updated] && [
      ...tablesData.machineryData[DataState.updated],
    ];

  const updateMachineryChangesItemsFiltered = updateMachineryChangesItems?.map(
    (machinaryUpdated: { unitId: string; newValue: number }) => {
      return {
        auxMachineUnitId: machinaryUpdated.unitId,
        newValue: machinaryUpdated.newValue,
      };
    }
  );

  const deleteMachineryChangesIds = tablesData?.machineryData &&
    tablesData.machineryData?.[DataState.deleted] && [
      ...tablesData.machineryData[DataState.deleted],
    ];

  // Log de cambios detectados en maquinaria
  if (
    addMachineryChangesItems?.length ||
    updateMachineryChangesItems?.length ||
    deleteMachineryChangesIds?.length
  ) {
    logMachineries('📊 Cambios en maquinaria detectados:');
    if (addMachineryChangesItems?.length) {
      logMachineries(`   ➕ A añadir: ${addMachineryChangesItems.length}`);
      addMachineryChangesItems.forEach((m: any, idx: number) => {
        logMachineries(`      [${idx + 1}] ID: ${m.id}, ut: ${m.unit}`);
      });
    }
    if (updateMachineryChangesItems?.length) {
      logMachineries(
        `   🔄 A actualizar: ${updateMachineryChangesItems.length}`
      );
      updateMachineryChangesItems.forEach((m: any, idx: number) => {
        logMachineries(
          `      [${idx + 1}] unitId: ${m.unitId}, newValue: ${m.newValue}`
        );
      });
    }
    if (deleteMachineryChangesIds?.length) {
      logMachineries(`   🗑️ A eliminar: ${deleteMachineryChangesIds.length}`);
      deleteMachineryChangesIds.forEach((id: string, idx: number) => {
        logMachineries(`      [${idx + 1}] ID: ${id}`);
      });
    }
  } else {
    logMachineries('ℹ️ No hay cambios en maquinaria');
  }

  // ========== WORK VEHICLES DATA (CONSTRUCTION) ==========
  // ⚠️ Los vehículos de obra NO tienen campo 'unit', son solo IDs directos
  // ⚠️ NO se "actualizan", solo se añaden o eliminan (workVehicleIDs)
  const addVehiclesChangesItems = tablesData?.vehiclesData &&
    tablesData.vehiclesData?.[DataState.added] && [
      ...tablesData.vehiclesData[DataState.added],
    ];

  const deleteVehiclesChangesIds = tablesData?.vehiclesData &&
    tablesData.vehiclesData?.[DataState.deleted] && [
      ...tablesData.vehiclesData[DataState.deleted],
    ];

  // Log de cambios en vehículos de obra
  if (addVehiclesChangesItems?.length || deleteVehiclesChangesIds?.length) {
    logVehicles('📊 Cambios en vehículos de obra detectados:');
    if (addVehiclesChangesItems?.length) {
      logVehicles(`   ➕ A añadir: ${addVehiclesChangesItems.length}`);
      addVehiclesChangesItems.forEach((v: any, idx: number) => {
        logVehicles(`      [${idx}] ID: ${v.id}`);
      });
    }
    if (deleteVehiclesChangesIds?.length) {
      logVehicles(`   🗑️ A eliminar: ${deleteVehiclesChangesIds.length}`);
      deleteVehiclesChangesIds.forEach((id: string, idx: number) => {
        logVehicles(`      [${idx}] ID: ${id}`);
      });
    }
  }

  // Suelta la fila de subidas de la Order (ver holdOrderUploads). Se declara
  // aquí para poder soltarla también desde el catch exterior.
  let releaseOrderUploads: (() => void) | null = null;
  // El fichero propio del croquis, SOLO si su subida se confirmó: al terminar
  // con éxito se borra de `canvas-action` (ver el final; Ruling 20).
  let uploadedSketchFile: string | null = null;
  // Las fotos nuevas ya están en la bandeja. Si después el guardado agota el
  // plazo, la edición que se encola no debe volver a entregarlas (spec
  // 2026-10-01-photo-outbox-identity § 10.4 E5).
  let photosHandedOff = false;
  try {
    if (orderActionData.id) {
      const {
        data: { order },
      } = await phaseClient.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: action.orderIDs[0],
        },
        // Fresca, como la de la Action: su estado decide si es un borrador
        // (`resolveValidationSave`) y si el aviso de cierre ya había saltado.
        fetchPolicy: 'network-only',
      });

      // T99 DEBUG: Log order state BEFORE mutations
      logSave('📋 [T99] Order state loaded (BEFORE mutations):', {
        orderId: order.id,
        orderState: order.state,
        orderRoid: order.roid,
      });

      const {
        data: { order: parentOrder },
      } = await phaseClient.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: order.parentExpeditionOrderIDs[0],
        },
      });
      // Ya no se borran external works ni vehicle occupants del "otro lado"
      // Ambos tipos pueden coexistir en la misma actuación

      // FOLD: las empresas externas (update/delete/create) se movieron DESPUÉS
      // del saveAction — mismo patrón que el resto de satélites que se
      // auto-conectan por actionId. Antes eran las únicas que iban antes.

      // Get order data for image naming
      let orderMetadata = null;
      if ((imagesToAdd || []).length || sketchToSaveOrUpdate) {
        const { data: orderData } = await phaseClient.query({
          query: GET_ORDER_BY_ID,
          variables: { id: orderActionData.orderId },
          fetchPolicy: 'cache-first',
        });

        if (orderData?.order) {
          orderMetadata = {
            indexExpedition: orderData.order.indexExpedition,
            indexType: orderData.order.indexType || 0,
            classification: orderData.order.classificationIDs?.[0]
              ? await getClassificationCode(
                  orderData.order.classificationIDs[0]
                )
              : '01',
            orderType: OrderType.Action,
          };
        }
      }

      // Offline-safe sketch upload: si Cloudinary falla, encolamos la subida
      // y NO borramos el sketch viejo hasta que el sync diferido tenga éxito.
      let imageArray: any[] = [];
      let cloudinaryFailedForSketch = false;
      // OUTBOX DE FOTOS (2026-08): las fotos se encolan SIEMPRE y suben en
      // segundo plano — ya no bloquean el guardado ni pueden tumbarlo. Este
      // fichero ya encolaba fotos, pero solo en la rama de rescate offline.
      // El borrado de imágenes aquí NO usa el patrón `removeImages` (usa
      // `imagesToDelete`, una lista explícita del change-tracking), así que no
      // hace falta el plan para nada más que registrar cuántas van.
      if ((imagesToAdd || []).length && orderMetadata) {
        // Misma carrera que en el asistente de creación: encolar dispara el
        // drenado y el createImage chocaba con el saveAction de abajo (P2034).
        releaseOrderUploads = holdOrderUploads(orderActionData.orderId);
        const plannedPhotos = await enqueuePhotosForUpload({
          picturesDirectoryName: OrderType.Action,
          orderId: orderActionData.orderId,
          orderType: OrderType.Action,
          entityType: 'action',
          orderMetadata,
          images: form.images,
          // 🔴 SOLO las nuevas. El formulario de edición carga en OPFS todas
          // las fotos que enseña, incluidas las que ya están en BD; sin este
          // filtro se re-subían y se creaba un registro Image duplicado por
          // cada una (visto en vivo: añadir 1 foto a una actuación con 30
          // disparaba «Subiendo fotos · 0 de 31»).
          onlyNames: (imagesToAdd as Array<{ id: string }>).map((i) => i.id),
          // En un reintento de la cola: si un intento anterior ya las entregó,
          // no se repiten (spec 2026-10-01-photo-outbox-identity § 10.4 E4).
          handoff: form.photoHandoff,
        });
        photosHandedOff = true;
        logSave(
          '📦 fotos encoladas para subida diferida: %d',
          plannedPhotos.length,
        );
      }

      if ((imagesToAdd || []).length || sketchToSaveOrUpdate) {
        // 🔴 El fichero de SU croquis, por nombre (Ruling 15; spec 2026-11-02
        // § 6): `canvas-action` la comparten las pestañas y crear y validar
        // actuación, y se subía «el primero de la carpeta» aunque fuera el
        // croquis de otra. Marca `fileName` → ese; `null` → ninguno; sin
        // marca y dibujo local (borrador o operación encolada de antes) → el
        // primero, lo de siempre (`sketchFile.ts`). En el reintento de la
        // cola, `form.images` es la copia de la operación. Si la operación se
        // encoló con las imágenes YA resueltas (el «flap»), no hay croquis que
        // subir ni que dar por perdido (Ruling 20).
        const imagesAlreadyResolved =
          opts?.isReplay === true &&
          form.store.getItem(VALIDATION_IMAGES_RESOLVED_SNAPSHOT) === 'true';
        const ownSketchFile = sketchToSaveOrUpdate && !imagesAlreadyResolved
          ? await draftSketchFileName(
              draftSketchFile(editionSketch),
              form.images,
              directoryNaming.CANVAS,
            )
          : null;
        try {
          // La subida también corre contra el plazo de la fase.
          imageArray = await raceWithDeadline(
            UploadImage(
            undefined,
            ownSketchFile ? directoryNaming.CANVAS : undefined,
            orderMetadata || undefined,
            {
              throwOnError: true,
              orderId: orderActionData.orderId,
              entityType: 'action',
              formImages: form.images,
              formStore: form.store,
              canvasFileName: ownSketchFile,
            },
            ),
            deadline,
          );
          // Subida CONFIRMADA: `UploadImage` solo devuelve el croquis cuando
          // el almacén aceptó el fichero.
          if (ownSketchFile && imageArray.some((img: Record<string, unknown>) => 'sketchSvg' in img)) {
            uploadedSketchFile = ownSketchFile;
          }
        } catch (uploadErr) {
          // Rescate offline al EDITAR/VALIDAR actuación (espejo del editar
          // accidente, bug At.0001979.0008.02): antes SOLO el croquis se
          // encolaba; una FOTO NORMAL caía en el `else` y se re-lanzaba → el
          // throw ocurría ANTES del SAVE_ACTION, así que NI la foto NI los
          // campos editados se encolaban → pérdida TOTAL del guardado offline.
          // Ahora, con red caída, encolamos croquis Y fotos en
          // offlineImageUploadQueue (kind 'sketch'/'photo' — ambos soportados
          // por syncImageUploadQueue) y NO re-lanzamos: el flujo llega al
          // SAVE_ACTION, que OfflineQueueLink encola, y el catch de offline
          // hace el redirect limpio. Al reconectar sincronizan las dos colas
          // (fotos → Hetzner + CREATE_NEW_IMAGE; campos → SaveAction).
          // Fuente única (E3): en Android manda el estado nativo.
          const isOffline = !isOnlineNow();
          const hasRegularPhotos = (imagesToAdd || []).length > 0;
          const canRescueOffline =
            // En replay las imágenes ya viven en op.localImages de la
            // compound (el reintento las recibe en memoria) — rescatarlas aquí
            // duplicaría por doble contabilidad compound×cola de imágenes.
            // Re-lanzar en su lugar.
            !opts?.isReplay &&
            uploadErr instanceof CloudinaryUploadError &&
            !!orderMetadata &&
            (sketchToSaveOrUpdate || (isOffline && hasRegularPhotos));

          if (!canRescueOffline) {
            // Error online real (4xx/5xx con red) o sin metadata → re-lanzar
            // para que el manejo de errores habitual lo muestre.
            throw uploadErr;
          }
          // Narrow para TS: canRescueOffline ya exige orderMetadata truthy.
          if (!orderMetadata) throw uploadErr;

          let rescuedSomething = false;
          try {
            // (a) Croquis — comportamiento previo intacto.
            if (sketchToSaveOrUpdate) {
              cloudinaryFailedForSketch = true;
              let files: { file: File; id: string }[] = [];
              try {
                files = await form.images.list(directoryNaming.CANVAS);
              } catch {
                files = [];
              }
              // El de este borrador, por nombre (nunca «el primero»).
              const sourceFile = ownSketchFile
                ? pickSketchFile(files, ownSketchFile)?.file
                : undefined;
              logSave(
                '🌐 Cloudinary failed offline — encolando sketch (sourceFile=%s)',
                sourceFile ? `${sourceFile.name} ${sourceFile.size}B` : 'NONE',
              );
              if (sourceFile) {
                const blobFileName = `pending_sketch_${orderActionData.orderId}_${Date.now()}.png`;
                // CRÍTICO: directorio dedicado para sobrevivir a cleanup
                // post-save (`removeNewActionStorage` borra `canvas-action`).
                await saveFilesIntoADirectory(OFFLINE_PENDING_UPLOADS_DIR, [
                  new File([sourceFile], blobFileName, {
                    type: sourceFile.type || 'image/png',
                  }),
                ]);
                const sketchData = getSketchFromSessionStorage('action', form.store);
                const queuedId = await addImageUploadOperation({
                  kind: 'sketch',
                  orderId: orderActionData.orderId,
                  orderType: OrderType.Action,
                  blobDirectory: OFFLINE_PENDING_UPLOADS_DIR,
                  blobFileName,
                  orderMetadata,
                  sketchData: {
                    svgContent: sketchData.svgContent,
                    fabricJson: sketchData.fabricJson,
                  },
                  sketchToRemoveId: sketchToRemove?.id,
                });
                rescuedSomething = true;
                logSave(
                  '📦 sketch encolado offline id=%s blob=%s/%s sketchToRemove=%s',
                  queuedId,
                  OFFLINE_PENDING_UPLOADS_DIR,
                  blobFileName,
                  sketchToRemove?.id ?? 'none',
                );
              } else {
                logSave(
                  '⚠️  Sin blob fuente en %s — sketch viejo se conserva, edición perdida',
                  directoryNaming.CANVAS,
                );
              }
            }

            // (b) Fotos normales: ya están en la bandeja —se entregan ARRIBA,
            // antes de subir el croquis—, así que cuentan como rescatadas y el
            // control de flujo queda como estaba. Antes se volvían a encolar
            // TODAS las de la carpeta del formulario, y en edición esa carpeta
            // también tiene las que ya están en el servidor: se duplicaban
            // (spec 2026-10-01-photo-outbox-identity § 10.2).
            if (isOffline && hasRegularPhotos && photosHandedOff) {
              rescuedSomething = true;
              logSave('📦 fotos ya en la bandeja; el rescate solo encola el croquis');
            }

            if (rescuedSomething) {
              window.dispatchEvent(
                new CustomEvent('offline-sync-error', {
                  detail: { message: i18n.t('errors:sketchPendingUpload') },
                }),
              );
            }
          } catch (queueErr) {
            Sentry.captureException(queueErr);
            logSave('⚠️  Error encolando imagen offline: %O', queueErr);
            // Si NO pudimos rescatar NADA, re-lanzar para no fingir éxito y
            // perder datos en silencio (mejor que el usuario vea el fallo).
            if (!rescuedSomething) {
              throw uploadErr;
            }
          }
          // Rescate OK → NO re-lanzar: el flujo continúa hasta el SAVE_ACTION
          // (campos editados → OfflineQueueLink lo encola → catch offline
          // redirige limpio). Las imágenes quedan en su cola y sincronizan al
          // reconectar.
        }
      }

      // FOLD (B2, At.0001979.0008.02): la CREACIÓN de imágenes va plegada en
      // `saveAction.input.images` (el backend crea las Image en su $transaction
      // → sin blob huérfano ni medio-registro). CREATE_NEW_IMAGE eliminado.
      // El array queda solo para los BORRADOS (DELETE_MANY_IMAGES, más abajo),
      // que siguen fuera del fold by-design.
      const imageArrayWithDeletes: Promise<unknown>[] = [];

      // T56 FIX: Eliminar sketch viejo si se actualizó con uno nuevo.
      // Invariante offline: si Cloudinary falló, NO borramos — quedará pendiente
      // del sync diferido (offlineImageUploadQueue.sketchToRemoveId).
      // Y si había croquis nuevo pero no llegó a subirse (sin fichero en la
      // carpeta: la subida no produce nada), tampoco: se perdería el croquis
      // (spec outbox-autocontenido v2 § 5.4).
      const newSketchUploaded = imageArray.some(
        (img: Record<string, unknown>) => 'sketchSvg' in img,
      );
      if (
        sketchToRemove?.id &&
        !cloudinaryFailedForSketch &&
        (!sketchToSaveOrUpdate || newSketchUploaded)
      ) {
        logSave('🗑️ Eliminando sketch viejo: %s', sketchToRemove.id);
        try {
          await phaseClient.mutate({
            mutation: DELETE_MANY_IMAGES,
            variables: {
              ids: [sketchToRemove.id],
            },
          });
        } catch (err) {
          logSave('⚠️ Error eliminando sketch viejo: %O', err);
        }
      } else if (sketchToRemove?.id && cloudinaryFailedForSketch) {
        logSave(
          '⏸️ DELETE diferido del sketch %s (Cloudinary offline, queued)',
          sketchToRemove.id,
        );
      } else if (sketchToRemove?.id) {
        logSave('⚠️ Croquis nuevo sin fichero: se conserva el viejo %s', sketchToRemove.id);
      }

      const deletedImages = imagesToDelete?.length;
      const addedImages = imagesToAdd?.length;

      client.cache.modify({
        fields: {
          orders(existingOrders = {}, { readField }) {
            const newOrderRef = {
              __ref: `Order:${orderActionData.orderId}`,
            };

            const images: readonly string[] | undefined = readField(
              'imageIDs',
              newOrderRef
            );

            if (images?.length === 0 && addedImages > 0) {
              const order = client.readQuery({
                query: GET_ORDER_BY_ID,
                variables: {
                  id: orderActionData.orderId,
                },
              });

              let mostCloseOrder: undefined | string;

              const dateOrder = new Date(order.order.creationDate);

              for (let i = 0; i < existingOrders?.orders?.length - 1; i++) {
                const idOrder = existingOrders?.orders[i]?.__ref.split(':')[1];

                const currentOrder = client.readQuery({
                  query: GET_ORDER_BY_ID,
                  variables: {
                    id: idOrder,
                  },
                });
                const currentOrderDate = new Date(
                  currentOrder?.order?.creationDate
                );

                if (dateOrder.getTime() > currentOrderDate.getTime()) {
                  mostCloseOrder =
                    existingOrders?.orders[i]?.__ref.split(':')[1];
                  break;
                }
              }

              if (!mostCloseOrder) {
                return {
                  ...existingOrders,
                };
              }

              const mostCloseOrderIndex = existingOrders.orders.findIndex(
                (item: Reference | StoreObject) =>
                  readField('id', item) === mostCloseOrder
              );
              const existingItems = existingOrders.orders.filter((item: any) => item !== null);

              existingItems.splice(mostCloseOrderIndex, 0, newOrderRef);

              const newOrdersArray = existingItems;

              const setCursorMultimedia = {
                ...cursorMultimedia,
                take: takeBase,
                cursor: '',
                orderBy: 'desc',
              };

              // En el reintento no se mueve el cursor de la tabla que esté mirando.
              if (existingOrders?.orders?.length > 0 && !opts?.isReplay) {
                dispatch(setCursor(tableIds.MULTIMEDIA, setCursorMultimedia));
              }
              return {
                ...existingOrders,
                orders: newOrdersArray,
                totalCount: existingOrders.totalCount + 1,
                pageInfo: {},
              };
            } else {
              return {
                ...existingOrders,
              };
            }
          },
        },
      });

      // FOLD: delete/create de external works movidos DESPUÉS del saveAction
      // (junto con el update) — ver bloque "Empresas externas" más abajo.

      imagesToDelete?.length
        ? imageArrayWithDeletes.push(
            phaseClient.mutate({
              mutation: DELETE_MANY_IMAGES,
              variables: {
                ids: imagesToDelete,
              },
              update(cache, { data: { deleteManyImagesByIds } }) {
                deleteManyImagesByIds.forEach(
                  (image: { id: Reference | StoreObject }) => {
                    cache.modify({
                      id: cache.identify(image.id),
                      fields: {
                        images(_, { DELETE }) {
                          return DELETE;
                        },
                      },
                    });
                  }
                );
              },
            })
          )
        : undefined;

      client.cache.modify({
        fields: {
          orders(existingOrders = {}, { readField }) {
            const newOrderRef = {
              __ref: `Order:${orderActionData.orderId}`,
            };
            const images: readonly string[] | undefined = readField(
              'imageIDs',
              newOrderRef
            );
            if (
              images?.length + addedImages - deletedImages === 0 &&
              existingOrders.orders.some((order: StoreObject | Reference) => {
                const ref = readField('id', order);
                return ref === orderActionData.orderId;
              })
            ) {
              const filterOrders = existingOrders.orders.filter((order: any) => {
                if (order === null) return false;
                const ref = readField('id', order as StoreObject | Reference);
                return ref !== orderActionData.orderId;
              });

              const setCursorMultimedia = {
                ...cursorMultimedia,
                take: takeBase,
                cursor: '',
                orderBy: 'desc',
              };

              // En el reintento no se mueve el cursor de la tabla que esté mirando.
              if (existingOrders?.orders?.length > 0 && !opts?.isReplay) {
                dispatch(setCursor(tableIds.MULTIMEDIA, setCursorMultimedia));
              }

              return {
                ...existingOrders,
                orders: filterOrders,
                totalCount: existingOrders.totalCount - 1,
                pageInfo: {},
              };
            } else {
              return {
                ...existingOrders,
              };
            }
          },
        },
      });

      await Promise.all(imageArrayWithDeletes);

      // ══ FOLD (At.0001979.0008.02) — pre-cómputo de las relaciones que viajan
      // DENTRO de saveAction. Todo se calcula ANTES de la mutación (en memoria
      // o con queries de lectura), nada depende de mutaciones intermedias.

      // C-11.13: parsear coordenadas si el usuario las marcó/cambió en el
      // LocationForm. JSON shape: `{ lat: number, lng: number }`.
      let parsedCoordinates: { lat: number; lng: number } | null = null;
      if (actionCoordinates) {
        try {
          parsedCoordinates = JSON.parse(actionCoordinates as string);
        } catch {
          parsedCoordinates = null;
        }
      }

      // workVehicleIds finales (vehículos de obra): cálculo puro en memoria —
      // los work vehicles son Vehicle EXISTENTES seleccionados, no units
      // creadas durante el save. El backend hace `set` (añade + quita).
      let finalWorkVehicleIds: string[] = action.workVehicleIDs || [];
      if (deleteVehiclesChangesIds?.length) {
        finalWorkVehicleIds = finalWorkVehicleIds.filter(
          (id: string) => !deleteVehiclesChangesIds.includes(id)
        );
      }
      if (addVehiclesChangesItems?.length) {
        finalWorkVehicleIds = [
          ...finalWorkVehicleIds,
          ...addVehiclesChangesItems.map((item: { id: string }) => item.id),
        ];
      }
      logVehicles(
        `✅ workVehicleIDs finales (fold): [${finalWorkVehicleIds.join(', ') || 'ninguno'}]`
      );

      // geoPosición: el backend en edit REEMPLAZA la geoposición (borra la
      // existente y crea la nueva en la misma tx) — semántica replace, no
      // patch. Para no perder lat/lng (u otros campos) cuando el usuario NO
      // toca el mapa, cargamos la geo existente como fallback. Solo se envía
      // si algo de posición cambió (misma condición que el viejo
      // UPDATE_POSITION_ACTION); `undefined` → el backend no la toca.
      const positionTouched = !!(
        pkInitRaw ||
        pkEndRaw ||
        track ||
        direction ||
        margin ||
        lane ||
        roadId ||
        concessionId ||
        parsedCoordinates
      );
      let geoPositionInput:
        | {
            pkInitKm: number;
            pkInitMeter: number;
            pkEndKm: number;
            pkEndMeter: number;
            track: string;
            direction?: string;
            margin?: string;
            lane: string;
            latitude?: number;
            longitude?: number;
            roadId: string;
            concessionId: string;
          }
        | undefined;
      if (positionTouched) {
        let existingGeo: {
          pkInitKm?: number;
          pkInitMeter?: number;
          pkEndKm?: number;
          pkEndMeter?: number;
          track?: string;
          direction?: string;
          margin?: string;
          lane?: string;
          latitude?: number;
          longitude?: number;
        } | null = null;
        const existingGeoId = action.geoPositionActionIDs?.[0];
        if (existingGeoId) {
          try {
            const { data: geoData } = await phaseClient.query({
              query: GET_POSITION_ACTIONS,
              variables: { id: existingGeoId },
              fetchPolicy: 'cache-first',
            });
            existingGeo = geoData?.geoPositionAction ?? null;
          } catch {
            existingGeo = null;
          }
        }
        const geoRoadId = roadId || action.roadIDs?.[0];
        const geoConcessionId = concessionId || action.concessionIDs?.[0];
        const geoTrack = track || existingGeo?.track || action.track;
        const geoLane = lane || existingGeo?.lane || action.lane;
        // Campos requeridos por SaveActionGeoPositionInput: si falta alguno
        // (action legacy sin datos), omitimos la geoposición (no-op, como
        // hacía el viejo update cuando no había geoPositionActionIDs[0]).
        if (geoRoadId && geoConcessionId && geoTrack && geoLane) {
          geoPositionInput = {
            pkInitKm: pkInitRaw
              ? Number(pkInitRaw[0])
              : (existingGeo?.pkInitKm ?? action.pkInitKm ?? 0),
            pkInitMeter: pkInitRaw
              ? Number(pkInitRaw[1])
              : (existingGeo?.pkInitMeter ?? action.pkInitMeter ?? 0),
            pkEndKm: pkEndRaw
              ? Number(pkEndRaw[0])
              : (existingGeo?.pkEndKm ?? action.pkEndKm ?? 0),
            pkEndMeter: pkEndRaw
              ? Number(pkEndRaw[1])
              : (existingGeo?.pkEndMeter ?? action.pkEndMeter ?? 0),
            track: geoTrack,
            direction: direction || existingGeo?.direction || action.direction,
            margin: margin || existingGeo?.margin || action.margin,
            lane: geoLane,
            latitude: parsedCoordinates?.lat ?? existingGeo?.latitude,
            longitude: parsedCoordinates?.lng ?? existingGeo?.longitude,
            roadId: geoRoadId,
            concessionId: geoConcessionId,
          };
        }
      }

      // Bloque C-5.3: SAVE_ACTION (update path con id) sustituye las 3 mutations
      // paralelas (UPDATE_ORDER_RELATIONSHIPS + UPDATE_ORDER + UPDATE_ACTION) por
      // una transacción atómica que mantiene Order ↔ Action coherentes —
      // elimina el deadlock P2034. T99: la response saveAction.action incluye
      // allActionsValidated y parentOrderId para el modal de cierre.
      // Un borrador (parcial) se completa a «Abierta» y no se valida en el mismo
      // guardado: el servidor rechaza parcial → validada (ver validationSaveState).
      const { state: saveActionState, isValidate: saveIsValidate } =
        resolveValidationSave({
          freshState: action.state,
          orderState: order.state,
          parentState: parentOrder.state,
          userRole,
          submitIntent,
        });

      // C-11.12: SAVE_ACTION incluye ahora activityId, assetId y elementOrderId.
      // Antes esos 3 viajaban por UPDATE_ACTION_RELATIONSHIPS post-Save (los 2
      // primeros) o se perdían (`elementOrder` no estaba en el schema). Tras
      // añadirlos al SaveActionInput, el front consolida toda la persistencia
      // Tipo B/C aquí en una sola transacción atómica Order↔Action.
      const saveActionResult = await phaseClient.mutate({
        mutation: SAVE_ACTION,
        variables: {
          // Actuació bàsica: fuera tripulación, vehículos, maquinaria y horas
          // — son del Recorregut de Vigilància. `vehicles: []` no era inocuo:
          // el backend lo lee como «borra los VehicleOccupant actuales».
          input: omitCrewFieldsForBasicAction({
            // Idempotencia online: UUID estable por operación lógica (RetryLink
            // reintenta con las mismas variables → mismo clientOpId → el backend
            // no duplica si el saveAction ya se aplicó).
            clientOpId: opts?.clientOpId ?? crypto.randomUUID(),
            id: orderActionData.id,
            state: saveActionState,
            registerInitDate: registerInitDate ? registerInitDate : undefined,
            // '' = papelera → null explícito (borra Order.registerEndDate /
            // Action.registerArrivalDate); ausente → undefined (no tocar).
            // El guard truthy anterior convertía '' en undefined y el borrado
            // nunca llegaba al backend.
            registerEndDate:
              registerEndDate === null
                ? undefined
                : registerEndDate === ''
                  ? null
                  : registerEndDate,
            registerArrivalDate:
              registerArrivalDate === null
                ? undefined
                : registerArrivalDate === ''
                  ? null
                  : registerArrivalDate,
            pkInitKm: pkInitRaw ? Number(pkInitRaw[0]) : undefined,
            pkInitMeter: pkInitRaw ? Number(pkInitRaw[1]) : undefined,
            pkEndKm: pkEndRaw ? Number(pkEndRaw[0]) : undefined,
            pkEndMeter: pkEndRaw ? Number(pkEndRaw[1]) : undefined,
            track: track ? track : undefined,
            direction: direction ? direction : action.direction,
            margin: margin ? margin : action.margin,
            lane: lane ? lane : undefined,
            concessionId: concessionId ? concessionId : undefined,
            roadId: roadId ? roadId : undefined,
            capitolId: editionCapitol ? editionCapitol : undefined,
            subCapitolId: editionSubCapitol ? editionSubCapitol : undefined,
            predefinedOrderId: editionPredefine ? editionPredefine : undefined,
            elementOrderId: editionElementOrder
              ? (editionElementOrder as string)
              : undefined,
            activityId: editionActivity
              ? (editionActivity as string)
              : undefined,
            assetId: editionAsset ? (editionAsset as string) : undefined,
            isValidate: saveIsValidate,
            descriptionValidated: editionDescription
              ? editionDescription
              : undefined,
            // Operario que registra editable (no obligatorio). Viaja solo si
            // cambió; el backend valida tenant y nunca obliga en update.
            registeredByUserId: registeredByUserId
              ? (registeredByUserId as string)
              : undefined,
            laneCutting: editionLaneCutting
              ? editionLaneCutting === 'true'
              : undefined,
            assetSwitch:
              editionAssetSwitch === null
                ? undefined
                : editionAssetSwitch === 'true',
            typeOfHour: hoursType ? hoursType : undefined,
            minutesTotals,
            isExternal,
            isSimultaneous,
            // ── FOLD (At.0001979.0008.02): operarios + vehículos (coche+
            // conductor) + workVehicles + geoPosición + imágenes consolidados
            // DENTRO de saveAction (una sola tx atómica). Antes iban por
            // CONNECT/DISCONNECT_MANY_USERS + NEW/DELETE_VEHICLE_OCCUPANT +
            // UPDATE_POSITION_ACTION + CREATE_NEW_IMAGE +
            // UPDATE_ACTION_RELATIONSHIPS — la cadena no-atómica del incidente.
            // userIds vacío → undefined (no tocar): réplica de la semántica
            // legacy — con el form vacío, el viejo UPDATE_ACTION_RELATIONSHIPS
            // hacía fallback a action.userIDs (los conservaba). `vehicles: []`
            // en cambio SÍ borra todos: el flujo viejo borraba todos los VO y
            // re-creaba solo los del form (vacío = quitar todos, by-design).
            userIds:
              newOperatorsIDs.length > 0 ? newOperatorsIDs : undefined,
            // Sin formulario de operarios → ausente = «no tocar».
            vehicles: crewFormMissing
              ? undefined
              : newVehiclesData.map(
                  (dv: { vehicleId: string; driverId: string }) => ({
                    vehicleId: dv.vehicleId,
                    driverId: dv.driverId,
                  })
                ),
            workVehicleIds: finalWorkVehicleIds,
            geoPosition: geoPositionInput,
            // FOLD (B2): imageArray sale de UploadImage (solo las NUEVAS
            // subidas a Hetzner) → el backend crea las Image en su tx.
            images: imageArray
              .filter((img) => !!img.storageKey)
              .map((img) => ({
                storageKey: img.storageKey,
                name: img.name,
                creatingDate: img.creatingDate,
                sketchSvg: img.sketchSvg ?? null,
                sketchFabricJson: img.sketchFabricJson ?? null,
              })),
          }, isBasicActionSubmit),
        },
        update(cache) {
          // Invalida informe Gestión Horaria + listas tras guardar/validar la
          // actuación: horas/operarios/externos cambian datos derivados que los
          // loaders cache-first no refrescan al volver (antes solo "Actualizar").
          evictDerivedCaches(cache);
        },
      });
      // La Order ya está escrita: las fotos encoladas pueden subir.
      releaseOrderUploads?.();
      releaseOrderUploads = null;

      // T99: Extract close confirmation data from SAVE_ACTION response.
      //
      // BUG-FIX 2026-05-08: el schema GraphQL `SaveActionResult` devuelve
      // `allActionsValidated` y `parentOrderId` a NIVEL ROOT del payload
      // (junto a `order` y `action`), NO dentro de `saveAction.action`.
      //
      //   type SaveActionResult {
      //     order: Order!
      //     action: Action!
      //     allActionsValidated: Boolean   # ← root-level
      //     parentOrderId: ID              # ← root-level
      //   }
      //
      // Antes: `saveAction.action.allActionsValidated` siempre devolvía
      // undefined → || false → false → modal T99 NUNCA se disparaba al
      // validar última action desde el wizard ActionsValidation.
      // El path correcto desde ActionSummaryPage (NewAction/ActionSummary)
      // ya leía bien (saveAction.allActionsValidated).
      logSave('🔍 [T99] SAVE_ACTION response:', {
        hasData: !!saveActionResult?.data,
        hasSaveAction: !!saveActionResult?.data?.saveAction,
        saveActionKeys: saveActionResult?.data?.saveAction
          ? Object.keys(saveActionResult.data.saveAction)
          : [],
      });

      const allActionsValidated =
        saveActionResult?.data?.saveAction?.allActionsValidated || false;
      const closeOrderParentId =
        saveActionResult?.data?.saveAction?.parentOrderId || null;

      logSave('🔍 [T99] Extracted values:', {
        allActionsValidated,
        closeOrderParentId,
        rawAllActionsValidated: saveActionResult?.data?.saveAction?.allActionsValidated,
        rawParentOrderId: saveActionResult?.data?.saveAction?.parentOrderId,
      });

      // T99: Store close confirmation data in Redux for ShowOT/ActionsValidation to show modal
      // IMPORTANTE: Solo mostrar modal si la acción CAMBIÓ de estado (no estaba ya validada)
      // Se mira el estado de la Order (type=Action), leído del servidor antes de
      // guardar: es contra el que valida el back.
      const actionWasAlreadyValidated = order.state === OrderState.Validated;

      // T99 DEBUG: Log guard check
      logSave('🔍 [T99] Guard check - actionWasAlreadyValidated:', {
        orderState: order.state,
        expectedValidatedState: OrderState.Validated,
        isAlreadyValidated: order.state === OrderState.Validated,
        actionWasAlreadyValidated,
      });

      // T99 DEBUG: Log all conditions before dispatch
      logSave('⚠️ [T99] Close modal dispatch conditions:', {
        allActionsValidated,
        closeOrderParentId,
        actionWasAlreadyValidated,
        shouldDispatch: allActionsValidated && closeOrderParentId && !actionWasAlreadyValidated,
        reason: actionWasAlreadyValidated
          ? 'Action was already validated - NO MODAL'
          : allActionsValidated
            ? 'State changed to validated - SHOW MODAL'
            : 'Not all actions validated yet',
      });

      if (allActionsValidated && closeOrderParentId && !actionWasAlreadyValidated) {
        // Format ROID for display (convert array to string if needed)
        const parentRoid = Array.isArray(parentOrder.roid)
          ? parentOrder.roid.join('.')
          : parentOrder.roid;

        logSave('🔔 [T99] All actions validated! Dispatching Redux action:', {
          allActionsValidated,
          closeOrderParentId,
          parentRoid,
          orderPreviousState: order.state,
          orderChangedToValidated: order.state !== OrderState.Validated,
        });
        console.log('🔔 [T99] All actions validated! Dispatching Redux action:', {
          allActionsValidated,
          closeOrderParentId,
          parentRoid,
          orderPreviousState: order.state,
          orderChangedToValidated: order.state !== OrderState.Validated,
        });
        // T99: Remove from declined list first (in case user said "No" before but now validated a NEW action)
        // This ensures the modal appears again when a new action is validated
        // Solo en pantalla: el aviso sale en el resumen de ESTA actuación. En el
        // reintento el operario está en otra parte y el aviso aparecería en el
        // resumen de otra actuación.
        if (!opts?.isReplay) {
          store.dispatch(orderCloseRemoveDeclined(closeOrderParentId));
          store.dispatch(orderCloseSetPending(closeOrderParentId, parentRoid));
        }
        logSave('✅ [T99] Redux action dispatched - orderCloseSetPending');
      } else {
        logSave('ℹ️ [T99] Conditions not met for close confirmation:', {
          allActionsValidated,
          closeOrderParentId,
          actionWasAlreadyValidated,
          reason: !allActionsValidated
            ? 'Not all actions validated yet'
            : actionWasAlreadyValidated
              ? 'Action was already validated (no state change)'
              : 'No parent order ID',
        });
      }

      // FOLD: UPDATE_POSITION_ACTION eliminado — la geoPosición viaja en
      // `saveAction.input.geoPosition` (replace atómico en el backend).

      // ══ Empresas externas (movidas AQUÍ desde antes del saveAction — mismo
      // patrón que el resto de satélites que se auto-conectan por actionId).
      if (worksToUpdate.length > 0) {
        // `await Promise.all` (no forEach-async): un fallo se propaga y aborta
        // en vez de tragarse (clase del incidente At.0001979.0008.02).
        await Promise.all(
          worksToUpdate.map((externalWorkToUpdate) =>
            phaseClient.mutate({
              mutation: UPDATE_EXTERNAL_WORKS,
              variables: {
                input: externalWorkToUpdate,
              },
            })
          )
        );
      }

      if (worksToDelete?.length) {
        await phaseClient.mutate({
          mutation: DELETE_EXTERNAL_WORKS_BY_IDS,
          variables: {
            ids: worksToDelete,
          },
        });
      }

      if (worksToAdd?.length) {
        // Desde 2026-07 el wizard NO da de alta empresas en el catálogo
        // maestro: el desplegable solo ofrece empresas ya existentes y activas,
        // así que TODAS las filas traen un id real. El alta vive en Gestió >
        // Empreses externes (y el backend la deniega a los roles operativos).
        await Promise.all(
          worksToAdd.map(async (work) =>
            phaseClient.mutate({
              mutation: CREATE_EXTERNAL_WORK,
              variables: {
                input: {
                  operatorsNumber: Number(work.operatorsNumber),
                  externalCompanyId: work.id,
                  actionId: orderActionData.id,
                },
              },
            })
          )
        );
      }

      await phaseClient.query({
        query: FETCH_EXTERNAL_COMPANIES,
        fetchPolicy: 'network-only',
      });

      // PASO 1: Ejecutar todas las operaciones DELETE primero
      logSave('🗑️ Ejecutando operaciones DELETE...');
      const deleteOperations = [];

      // FOLD: DISCONNECT_MANY_USERS y DELETE_MANY_VEHICLE_OCCUPANTS eliminados
      // — saveAction hace el set-reconcile de operarios y el reemplazo total
      // de VehicleOccupants dentro de su tx.

      // Eliminar materiales
      if (deleteMaterialsChangesIds?.length) {
        deleteOperations.push(
          phaseClient.mutate({
            mutation: DELETE_MANY_MATERIALS,
            variables: {
              ids: deleteMaterialsChangesIds,
            },
            update(cache, { data: { deleteManyMaterialUnits } }) {
              deleteManyMaterialUnits.forEach(
                (materialUnit: { id: Reference | StoreObject }) => {
                  cache.modify({
                    id: cache.identify(materialUnit.id),
                    fields: {
                      materialUnits(_, { DELETE }) {
                        return DELETE;
                      },
                    },
                  });
                }
              );
            },
          })
        );
        logMaterials(
          `🗑️ Eliminando ${deleteMaterialsChangesIds.length} materiales`
        );
        logMaterials(`   IDs: ${deleteMaterialsChangesIds.join(', ')}`);
      }

      // Eliminar maquinaria
      if (deleteMachineryChangesIds?.length) {
        deleteOperations.push(
          phaseClient.mutate({
            mutation: DELETE_MANY_AUX_MACHINE_UNITS,
            variables: {
              ids: deleteMachineryChangesIds,
            },
            update(cache, { data: { deleteManyAuxMachineUnits } }) {
              deleteManyAuxMachineUnits.forEach(
                (auxMachineUnit: { id: Reference | StoreObject }) => {
                  cache.modify({
                    id: cache.identify(auxMachineUnit.id),
                    fields: {
                      auxMachineUnits(_, { DELETE }) {
                        return DELETE;
                      },
                    },
                  });
                }
              );
            },
          })
        );
        logMachineries(
          `🗑️ Eliminando ${deleteMachineryChangesIds.length} maquinarias`
        );
        logMachineries(`   IDs: ${deleteMachineryChangesIds.join(', ')}`);
      }

      // ========== MARCAR VEHÍCULOS DE OBRA PARA ELIMINAR ==========
      // ⚠️ NO eliminamos aquí, solo registramos para consolidar al final
      if (deleteVehiclesChangesIds?.length) {
        logVehicles(
          `🗑️ Marcando ${deleteVehiclesChangesIds.length} vehículos de obra para eliminar`
        );
        logVehicles(
          `   IDs a eliminar: ${deleteVehiclesChangesIds.join(', ')}`
        );
      }

      // Eliminar animales
      if (animalsToDelete?.length) {
        deleteOperations.push(
          phaseClient.mutate({
            mutation: DELETE_MANY_ANIMAL_UNITS,
            variables: {
              ids: animalsToDelete,
            },
            update(cache, { data: { deleteManyAnimalUnits } }) {
              deleteManyAnimalUnits.forEach(
                (animalUnit: { id: Reference | StoreObject }) => {
                  cache.modify({
                    id: cache.identify(animalUnit.id),
                    fields: {
                      animalUnits(_, { DELETE }) {
                        return DELETE;
                      },
                    },
                  });
                }
              );
            },
          })
        );
        logAnimals(`🗑️ Eliminando ${animalsToDelete.length} animales`);
        logAnimals(`   IDs: ${animalsToDelete.join(', ')}`);
      }

      // Ejecutar todas las operaciones de eliminación
      if (deleteOperations.length > 0) {
        logSave(
          `🗑️ Ejecutando ${deleteOperations.length} operaciones DELETE...`
        );
        await Promise.all(deleteOperations);
        logSave('✅ Operaciones DELETE completadas');
      } else {
        logSave('ℹ️ No hay operaciones DELETE para ejecutar');
      }

      // PASO 2: Ejecutar todas las operaciones CREATE/ADD
      logSave('➕ Ejecutando operaciones CREATE/ADD...');
      const createOperations: Promise<unknown>[] = [];

      // FOLD: CONNECT_MANY_USERS y el loop NEW_VEHICLE_OCCUPANT_ACTION
      // eliminados — operarios (userIds set-reconcile) y VehicleOccupants
      // ({vehicleId, driverId} reemplazo total) los persiste saveAction en su
      // tx. El refetch de la action al final refresca la caché.

      // Añadir animales
      if (animalsToAdd.length > 0) {
        logAnimals(`➕ Añadiendo ${animalsToAdd.length} animales`);
        animalsToAdd.forEach((item, index) => {
          logAnimals(
            `   [${index + 1}] ID: ${item.id}, UnidadAnimal: ${
              item.ut
            }, Estado: ${item.statusId || 'N/A'}`
          );
          createOperations.push(
            phaseClient.mutate({
              mutation: CREATE_NEW_ANIMAL_UNIT,
              variables: {
                input: {
                  unit: Number(item.ut),
                  actionId: orderActionData.id,
                  animalId: item.id,
                  animalStatusId: item.statusId,
                },
              },
              update(cache, { data: { createAnimalUnit } }) {
                cache.modify({
                  fields: {
                    animalUnits(existingAnimalUnits = []) {
                      const newAnimalUnitRef = cache.writeFragment({
                        data: createAnimalUnit,
                        fragment: NEW_ANIMAL_UNIT,
                      });
                      return [...existingAnimalUnits, newAnimalUnitRef];
                    },
                  },
                });
              },
            })
          );
        });
      }

      // Añadir materiales
      if (addMaterialsChangesItems?.length) {
        logMaterials(
          `➕ Añadiendo ${addMaterialsChangesItems.length} materiales`
        );
        addMaterialsChangesItems.forEach(
          (item: { unit: number; id: string }, index: number) => {
            logMaterials(
              `   [${index + 1}] ID: ${item.id}, Unidades: ${item.unit}`
            );
            createOperations.push(
              phaseClient.mutate({
                mutation: CREATE_NEW_MATERIAL_UNIT,
                variables: {
                  input: {
                    unit: item.unit,
                    actionId: orderActionData.id,
                    materialId: item.id,
                  },
                },
                update(cache, { data: { createMaterialUnit } }) {
                  cache.modify({
                    fields: {
                      materialUnits(existingMaterialUnits = []) {
                        const newMaterialUnitRef = cache.writeFragment({
                          data: createMaterialUnit,
                          fragment: NEW_MATERIAL_UNIT,
                        });
                        return [...existingMaterialUnits, newMaterialUnitRef];
                      },
                    },
                  });
                },
              })
            );
          }
        );
      }

      // Añadir maquinaria
      if (addMachineryChangesItems?.length) {
        logMachineries(
          `➕ Añadiendo ${addMachineryChangesItems.length} maquinarias`
        );
        addMachineryChangesItems.forEach(
          (item: { unit: number; id: string }, index: number) => {
            logMachineries(
              `   [${index + 1}] ID: ${item.id}, Unidades: ${item.unit}`
            );
            createOperations.push(
              phaseClient.mutate({
                mutation: CREATE_NEW_AUX_MACHINE_UNIT,
                variables: {
                  input: {
                    unit: item.unit,
                    actionId: orderActionData.id,
                    auxMachineId: item.id,
                  },
                },
                update(cache, { data: { createAuxMachineUnit } }) {
                  cache.modify({
                    fields: {
                      auxMachineUnits(existingAuxMachineUnits = []) {
                        const newAuxMachineRef = cache.writeFragment({
                          data: createAuxMachineUnit,
                          fragment: NEW_AUX_MACHINE_UNIT,
                        });
                        return [...existingAuxMachineUnits, newAuxMachineRef];
                      },
                    },
                  });
                },
              })
            );
          }
        );
      }

      // ========== MARCAR VEHÍCULOS DE OBRA PARA AÑADIR ==========
      // ⚠️ NO añadimos aquí, solo registramos para consolidar al final
      if (addVehiclesChangesItems?.length) {
        logVehicles(
          `➕ Marcando ${addVehiclesChangesItems.length} vehículos de obra para añadir`
        );
        const newVehicleIds = addVehiclesChangesItems.map(
          (item: { id: string }) => item.id
        );
        logVehicles(`   IDs nuevos: ${newVehicleIds.join(', ')}`);
      }

      // Ejecutar todas las operaciones de creación
      if (createOperations.length > 0) {
        logSave(
          `➕ Ejecutando ${createOperations.length} operaciones CREATE/ADD...`
        );
        await Promise.all(createOperations);
        logSave('✅ Operaciones CREATE/ADD completadas');
      } else {
        logSave('ℹ️ No hay operaciones CREATE/ADD para ejecutar');
      }

      // PASO 3: Ejecutar todas las operaciones UPDATE
      logSave('🔄 Ejecutando operaciones UPDATE...');
      const updateOperations = [];

      // Actualizar maquinaria
      if (updateMachineryChangesItems?.length) {
        logMachineries(
          `🔄 Actualizando ${updateMachineryChangesItems.length} maquinarias`
        );
        updateMachineryChangesItemsFiltered.forEach(
          (
            item: { auxMachineUnitId: string; newValue: number },
            index: number
          ) => {
            logMachineries(
              `   [${index + 1}] ID: ${item.auxMachineUnitId}, Nuevo valor: ${
                item.newValue
              }`
            );
          }
        );
        updateOperations.push(
          phaseClient.mutate({
            mutation: UPDATE_MANY_AUX_MACHINE_UNITS,
            variables: {
              input: updateMachineryChangesItemsFiltered,
            },
          })
        );
      }

      // Actualizar materiales
      if (updateMaterialsChangesItems?.length) {
        logMaterials(
          `🔄 Actualizando ${updateMaterialsChangesItems.length} materiales`
        );
        updateMaterialsChangesItemsFiltered.forEach(
          (
            item: { materialUnitId: string; newValue: number },
            index: number
          ) => {
            logMaterials(
              `   [${index + 1}] ID: ${item.materialUnitId}, Nuevo valor: ${
                item.newValue
              }`
            );
          }
        );
        updateOperations.push(
          phaseClient.mutate({
            mutation: UPDATE_MANY_MATERIALS_UNITS,
            variables: {
              input: updateMaterialsChangesItemsFiltered,
            },
          })
        );
      }

      // Actualizar animales
      if (animalsToUpdate?.length) {
        logAnimals(`🔄 Actualizando ${animalsToUpdate.length} animales`);
        animalsToUpdate.forEach(
          (
            item: {
              animalUnitId: string;
              newValue?: number;
              statusId?: string;
            },
            index: number
          ) => {
            const updateInfo = [];
            if (item.newValue !== undefined)
              updateInfo.push(`Unidades: ${item.newValue}`);
            if (item.statusId !== undefined)
              updateInfo.push(`Estado: ${item.statusId}`);
            logAnimals(
              `   [${index + 1}] ID: ${item.animalUnitId}, ${updateInfo.join(
                ', '
              )}`
            );
          }
        );
        updateOperations.push(
          phaseClient.mutate({
            mutation: UPDATE_MANY_ANIMAL_UNITS,
            variables: {
              input: animalsToUpdate,
            },
          })
        );
      }

      // Ejecutar todas las operaciones de actualización
      if (updateOperations.length > 0) {
        logSave(
          `🔄 Ejecutando ${updateOperations.length} operaciones UPDATE...`
        );
        await Promise.all(updateOperations);
        logSave('✅ Operaciones UPDATE completadas');
      } else {
        logSave('ℹ️ No hay operaciones UPDATE para ejecutar');
      }

      // ══ FOLD: UPDATE_ACTION_RELATIONSHIPS ELIMINADO (y con él las 2
      // re-queries network-only intermedias que necesitaba para leer los IDs
      // recién creados). Cobertura actual:
      //  - users/vehicleOccupants/workVehicles/geoPosition → saveAction (tx).
      //  - auxMachineUnits → sus CREATE se auto-conectan por actionId y su
      //    deleteMany hace `actions: { disconnect }` en el backend (verificado
      //    en auxMachineUnit.service.deleteMany) → el set era redundante.

      // Escrituras hechas: de aquí en adelante un fallo de red NO puede
      // re-encolar (repetiría lo ya guardado). Lo que queda son refrescos.
      writesCommitted = true;

      logSave('🔍 Consultando datos actualizados...');
      const {
        data: { action: actionUpdated },
      } = await phaseClient.query({
        fetchPolicy: 'network-only',
        query: GET_ACTION_BY_ID,
        variables: { id: orderActionData.id },
      });

      await Promise.all(
        actionUpdated.vehicleOccupantIDs.map((vehicleOccupantId: string) =>
          phaseClient.query({
            query: GET_VEHICLE_OCCUPANT_BY_ID,
            variables: {
              id: vehicleOccupantId,
            },
            fetchPolicy: 'network-only',
          })
        )
      );

      // (fix) El array del map iba ANIDADO dentro de Promise.all → las queries
      // de usuarios no se esperaban realmente (fire-and-forget de facto).
      await Promise.all([
        ...actionUpdated.userIDs.map((operatorId: string) =>
          phaseClient.query({
            fetchPolicy: 'network-only',
            query: GET_USER_BY_ID,
            variables: {
              input: {
                id: operatorId,
              },
            },
          })
        ),

        phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_ORDER_BY_ID,
          variables: { id: orderActionData.orderId },
        }),
      ]);

      if (editionActivity) {
        await phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_ACTIVITY_BY_ID,
          variables: { id: editionActivity },
        });
      }
      if (editionAsset) {
        await phaseClient.query({
          fetchPolicy: 'network-only',
          query: GET_ASSET_BY_ID,
          variables: { id: editionAsset },
        });
      }
    }
  } catch (err) {
    releaseOrderUploads?.();
    releaseOrderUploads = null;
    // Offline: la mutación quedó ENCOLADA por OfflineQueueLink — no es un fallo.
    if (isOfflineQueuedError(err)) {
      if (opts?.isReplay) {
        // En replay, "continuar" haría que el sync manager borrara la op
        // compound sin que el resto de la cadena se hubiera ejecutado. Re-lanzar
        // → el catch de syncCompoundQueue (isValidationError=false) lo trata
        // como transient → la op se conserva y reintenta con el MISMO clientOpId.
        throw err;
      }

      // GAP 6 — flap ONLINE a mitad del guardado. La mutación en curso
      // (SAVE_ACTION o un unit) la encoló OfflineQueueLink, pero los satélites
      // que venían DESPUÉS en el try quedaban sin ejecutar NI encolar → se
      // perdían (antes el flujo "continuaba con budget items" y redirigía como
      // éxito: guardado parcial silencioso, clase At.0001979.0008.02). Ahora
      // capturamos una operación compound 'action-edit' que, al reconectar,
      // re-ejecuta el saveData ENTERO idempotentemente (SAVE_ACTION set-absoluto,
      // units find-or-create, deleteMany tombstone → convergen con lo ya
      // encolado en la cola simple).
      //
      // localImages: [] A PROPÓSITO. En este punto las imágenes ya están
      // resueltas: o se subieron online antes del flap, o el bloque de imágenes
      // las rescató a la cola de imágenes (OFFLINE_PENDING_UPLOADS_DIR).
      // Recapturarlas del OPFS y re-subirlas en el replay las DUPLICARÍA
      // (createImage sólo deduplica con el MISMO clientOpId; el replay generaría
      // uno nuevo) → el compound NO lleva imágenes.
      //
      // return (no caer al bloque de partidas de abajo): las partidas se
      // ejecutan SÓLO dentro del compound → una vez, sin duplicar contra la cola
      // simple (ADD_BUDGET_ITEM no es idempotente).
      logSave(
        '📴 Flap online a mitad del guardado — capturando compound action-edit'
      );

      // Sin imágenes A PROPÓSITO: aquí ya están resueltas (subidas online o en
      // la cola de imágenes); recapturarlas las duplicaría en el replay.
      const { redirectTo } = await queueValidationEdit({
        formData,
        params,
        orderId: orderActionData.orderId as string,
        orderRoid: orderActionData.roid ?? '',
        actionId: orderActionData.id as string,
        withImages: false,
      });
      window.dispatchEvent(new CustomEvent('offline-action-queued'));
      return redirect(redirectTo);
    } else if (!opts?.isReplay && isTransportFailure(err) && !writesCommitted) {
      // RED DEGRADADA (spec 2026-09-11 § 1 E5): el plazo de la fase venció o el
      // transporte falló, y todavía no se había confirmado ninguna escritura.
      // Se encola lo mismo que la rama sin red y se avisa. Antes este camino no
      // existía: con cobertura mala el guardado se quedaba colgado sin techo
      // (medido en tablet: 6 min sin avisar, con el backend ya guardado).
      logSave('⌛ Plazo de la fase vencido o transporte caído — encolando');
      const { redirectTo } = await queueValidationEdit({
        formData,
        params,
        orderId: orderActionData.orderId as string,
        orderRoid: orderActionData.roid ?? '',
        actionId: orderActionData.id as string,
        withImages: true,
        // Si las fotos ya se entregaron, la edición las lleva igual pero su
        // reintento no las repite (spec 2026-10-01-photo-outbox-identity § 10.4 E5).
        photosHandedOff,
      });
      window.dispatchEvent(new CustomEvent('offline-action-queued'));
      throw createOfflineQueuedError({ redirectTo });
    } else {
      // ANTES se tragaba CUALQUIER error (Sentry+log) y el flujo continuaba a
      // budget items + limpieza + redirect "éxito" → guardado inconsistente
      // con pinta de OK (clase del incidente At.0001979.0008.02). Re-lanzamos:
      // el catch del handler (action.ts, C-11.9) captura, reporta a Sentry y
      // muestra el error al usuario.
      console.log('> Error updating action', err);
      throw err;
    }
  }

  // 📋 PROCESAR PARTIDAS PRESUPUESTARIAS (PATRÓN ESTANDARIZADO CON BATCH OPERATIONS)

  // CREAR nuevas partidas (INDIVIDUAL - backend no tiene mutación batch)
  if (addBudgetItemsChangesItems && addBudgetItemsChangesItems.length > 0) {
    logSave(
      `🔄 Creando ${addBudgetItemsChangesItems.length} nuevas partidas (INDIVIDUAL)`
    );

    // CRÍTICO: el try/catch va DENTRO del for, no fuera. Si lo envolvemos
    // alrededor del loop completo, la PRIMERA mutation que se encole offline
    // (rechaza con `offlineQueued: true`) interrumpe el for y los items
    // 2..N nunca se procesan. Fuera, el usuario añade 5 budgets y solo se
    // encola 1; al reconectar se sincroniza solo el primero y los demás se
    // pierden. Cada mutate debe ser independiente.
    let queuedOffline = 0;
    for (const item of addBudgetItemsChangesItems) {
      logSave(
        `🔄 Creando partida individual:`,
        JSON.stringify(item, null, 2)
      );
      try {
        const result = await phaseClient.mutate({
          mutation: ADD_BUDGET_ITEM_TO_ACTION,
          variables: {
            actionId: orderActionData.id,
            budgetItemId: item.id, // Para items nuevos, el id ES el budgetItemId
            quantity: item.quantity,
          },
        });
        logSave(`✅ Partida creada exitosamente:`, result);
      } catch (error) {
        if (isOfflineQueuedError(error)) {
          if (opts?.isReplay) throw error; // replay: conservar la compound, no per-item
          // Encolada por OfflineQueueLink — Apollo replay-eará al reconectar.
          // Continuamos con los siguientes items (cada uno se encola por
          // separado en `rois_offline_mutation_queue`).
          queuedOffline++;
          logSave(
            '📦 ADD_BUDGET_ITEM_TO_ACTION (item %s) encolada offline — continuando con siguiente',
            item.id,
          );
        } else {
          logSave('❌ Error detallado creando partida %s: %O', item.id, error);
          console.error('❌ Error creando partida presupuestaria:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    }
    if (queuedOffline > 0) {
      logSave(
        `📦 ${queuedOffline}/${addBudgetItemsChangesItems.length} ADD partidas encoladas offline`
      );
    } else {
      logSave(
        `✅ ${addBudgetItemsChangesItems.length} partidas creadas exitosamente`
      );
    }
  }

  // ACTUALIZAR cantidades de partidas existentes (BATCH)
  // Verificando actualizaciones de partidas

  if (
    updateBudgetItemsChangesItems &&
    updateBudgetItemsChangesItems.length > 0
  ) {
    logSave(
      'Updating %d budget items individually',
      updateBudgetItemsChangesItems.length
    );

    // try/catch DENTRO del for — cada UPDATE se encola por separado offline.
    let queuedOffline = 0;
    for (const item of updateBudgetItemsChangesItems) {
      try {
        const result = await phaseClient.mutate({
          mutation: UPDATE_ACTION_BUDGET_ITEM_QUANTITY,
          variables: {
            id: item.id, // actionBudgetItemId
            quantity: item.quantity,
          },
        });
        logSave('Budget item updated successfully: %s', item.id);
      } catch (error) {
        if (isOfflineQueuedError(error)) {
          if (opts?.isReplay) throw error; // replay: conservar la compound, no per-item
          queuedOffline++;
          logSave(
            '📦 UPDATE_ACTION_BUDGET_ITEM_QUANTITY (item %s) encolada offline',
            item.id,
          );
        } else {
          logSave('Error updating budget item %s: %O', item.id, error);
          console.error('❌ Error actualizando partida presupuestaria:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    }
    if (queuedOffline > 0) {
      logSave(
        `📦 ${queuedOffline}/${updateBudgetItemsChangesItems.length} UPDATE partidas encoladas offline`
      );
    } else {
      logSave(
        'Successfully updated %d budget items',
        updateBudgetItemsChangesItems.length
      );
    }
  } else {
    logSave('No budget items to update');
  }

  // ELIMINAR partidas (INDIVIDUAL - backend no tiene mutación batch)
  if (deleteBudgetItemsChangesIds && deleteBudgetItemsChangesIds.length > 0) {
    logSave(
      `🔄 Eliminando ${deleteBudgetItemsChangesIds.length} partidas (INDIVIDUAL)`
    );
    logSave('🔍 IDs a eliminar:', deleteBudgetItemsChangesIds);

    // try/catch DENTRO del for — cada DELETE se encola por separado offline.
    const results = [];
    let queuedOffline = 0;
    for (const id of deleteBudgetItemsChangesIds) {
      logSave(`🗑️ Eliminando partida individual: ${id}`);
      try {
        const result = await phaseClient.mutate({
          mutation: REMOVE_BUDGET_ITEM_FROM_ACTION,
          variables: {
            id: id,
          },
        });
        results.push(result);
        logSave(`✅ Partida ${id} eliminada exitosamente`);
      } catch (error) {
        if (isOfflineQueuedError(error)) {
          if (opts?.isReplay) throw error; // replay: conservar la compound, no per-item
          queuedOffline++;
          logSave(
            '📦 REMOVE_BUDGET_ITEM_FROM_ACTION (id %s) encolada offline',
            id,
          );
        } else {
          logSave('❌ Error detallado eliminando partida %s: %O', id, error);
          console.error('❌ Error eliminando partida presupuestaria:', error);
          Sentry.captureException(error);
          throw error;
        }
      }
    }
    if (queuedOffline > 0) {
      logSave(
        `📦 ${queuedOffline}/${deleteBudgetItemsChangesIds.length} REMOVE partidas encoladas offline`
      );
    } else {
      logSave('✅ Resultados de eliminación:', results);
      logSave(
        `✅ ${deleteBudgetItemsChangesIds.length} partidas eliminadas exitosamente`
      );
    }
  }

  // Limpiar formularios base
  Object.values(formActionValidationNaming).forEach((key) =>
    form.store.removeItem(key)
  );

  // BUG #13 FIX: Actualizar isActionEdited a 'true' para que el resumen muestre "Guardar"
  // Esto permite salir del flujo correctamente en vez de crear un bucle infinito

  // FIX: Preservar contexto de navegación para redirección context-aware
  // Lee los datos existentes ANTES de sobrescribir para mantener origin, parentOrderId, etc.
  const existingDataRaw = form.store.getItem(formActionNaming.NEW_VALIDATION_ACTION_DATA);
  let existingNavigationContext: {
    origin?: string;
    parentOrderId?: string;
    parentRoid?: string;
    entityRoid?: string;
    returnTo?: string;
  } = {};

  if (existingDataRaw) {
    try {
      const parsed = JSON.parse(decompressSessionStorage(existingDataRaw));
      // Preservar SOLO los campos de navegación
      existingNavigationContext = {
        origin: parsed.origin,
        parentOrderId: parsed.parentOrderId,
        parentRoid: parsed.parentRoid,
        entityRoid: parsed.entityRoid,
        returnTo: parsed.returnTo,
      };
    } catch (e) {
      console.error('[saveData] Error reading existing navigation context:', e);
    }
  }

  form.store.setItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA,
    compressSessionStorage(
      JSON.stringify({
        id: orderActionData.id,
        orderId: orderActionData.orderId,
        roid: orderActionData.roid,
        isActionEdited: 'true',
        ...existingNavigationContext, // Preservar contexto de navegación
      })
    )
  );
  form.store.removeItem(formActionNaming.NEW_ACTION_DATA);
  form.store.removeItem(externalCompaniesNaming.EXTERNAL_WORKS_TO_ADD);
  form.store.removeItem(externalCompaniesNaming.EXTERNAL_WORKS_TO_DELETE);

  // ========== LIMPIAR FORMULARIOS TEMPORALES CON SUFIJOS ==========
  // Formularios de operadores (tabs interno/externo + caché de conductores
  // por-wizard — BUG borrador RV 2026-07-12)
  form.store.removeItem(
    `${formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM}_interno`
  );
  form.store.removeItem(
    `${formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM}_externals`
  );
  form.store.removeItem(
    `${formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM}_drivers`
  );

  // Formularios de equipamiento (añadir vehículo/maquinaria)
  form.store.removeItem(
    `${formActionValidationNaming.NEW_VALIDATION_WORK_EQUIPMENT_FORM}-vehicle`
  );
  form.store.removeItem(
    `${formActionValidationNaming.NEW_VALIDATION_WORK_EQUIPMENT_FORM}-machinery`
  );

  form.images.clear(OrderType.Action);
  // 🔴 El fichero propio del croquis, por nombre, solo si su subida se
  // confirmó y el guardado llegó hasta aquí (Ruling 20). Antes cada dibujo
  // vaciaba `canvas-action` y como mucho quedaba uno; desde el Ruling 15 cada
  // borrador deja el suyo, y quien solo valida acumulaba un PNG por validación
  // que `captureLocalImages` copiaba en cada operación encolada. Las copias de
  // validación ya se borraron arriba: nadie lo lista. Si la subida no se
  // confirmó (rescate sin red) o la operación fue a la cola, no se llega aquí
  // con él y se queda (la cola lo necesita).
  await removeDraftSketchFile(form.images, directoryNaming.CANVAS, uploadedSketchFile);

  // Efecto de PANTALLA: en el reintento, el operario puede estar en otro
  // formulario de actuación y le reiniciaría las pestañas.
  if (!opts?.isReplay) store.dispatch(actionTabsReset() as any);

  deadline.clear();
  return redirect(`../../summary/${orderActionData.id}`);
};
